<script setup lang="ts">
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  Archive, Bell, BookOpenText, Check, ChevronDown, ChevronRight, Clapperboard,
  Clock3, Copy, Download, Eye, EyeOff, FileText, Film, FolderOpen, History, Image, LayoutDashboard, Maximize2,
  LockKeyhole, LogOut, Menu, MessageSquareText, MoreHorizontal, Plus, RefreshCw,
  RotateCcw, Search, Settings, ShieldCheck, Sparkles, Trash2, Upload, UserCog, Users, X, PencilLine,
} from 'lucide-vue-next';
import { api, clearStoredToken, getStoredLoginEmail, getStoredToken, storeLoginEmail, storeToken } from './api';
import { characterProfileFallbacks } from './characterProfiles';
import type { Asset, BootstrapData, CharacterProfile, Episode, Member, MemberRole, PromptTemplate, ScriptMode, View } from './types';

const view = ref<View>('dashboard');
const mobileMenu = ref(false);
const loading = ref(true);
const loadError = ref('');
const episodes = ref<Episode[]>([]);
const assets = ref<Asset[]>([]);
const members = ref<Member[]>([]);
const activities = ref<string[]>([]);
const selectedEpisodeId = ref(1);
const scriptMode = ref<ScriptMode>('draft');
const editing = ref(false);
const draftBuffer = ref('');
const assetFilter = ref('全部');
const inviteEmail = ref('');
const inviteRole = ref<MemberRole>('编辑人员');
const toast = ref('');
const busy = ref(false);
const uploading = ref(false);
const uploadLabel = ref('');
const fileInput = ref<HTMLInputElement>();
const uploadDialogOpen = ref(false);
const uploadNoFileConfirmOpen = ref(false);
const uploadFilesDraft = ref<File[]>([]);
const uploadDragging = ref(false);
const uploadDraft = ref({ category: '人物', title: '', description: '' });
const selectedAsset = ref<Asset | null>(null);
const currentUser = ref<Member | null>(null);
const loginEmail = ref(getStoredLoginEmail());
const loginPassword = ref('');
const rememberLogin = ref(true);
const showPassword = ref(false);
const loginBusy = ref(false);
const loginError = ref('');
const showUserMenu = ref(false);
const isAdmin = computed(() => currentUser.value?.role === '管理员');
const canEditContent = computed(() => currentUser.value?.role === '管理员' || currentUser.value?.role === '编辑人员');
const adminTab = ref<'members' | 'reviews'>('members');
const reviewEpisodeId = ref(1);
type CharacterEditDraft = Omit<CharacterProfile, 'personality' | 'talents' | 'weaknesses' | 'habits' | 'appearance' | 'signatureItems' | 'relationships'> & {
  personality: string;
  talents: string;
  weaknesses: string;
  habits: string;
  appearance: string;
  signatureItems: string;
  relationships: string;
};
function createEmptyCharacterDraft(): CharacterEditDraft {
  return {
    name: '', englishName: '', role: '', age: 0, birthday: '', zodiac: '', height: '',
    hometown: '', education: '', occupation: '', quote: '', bio: '',
    personality: '', talents: '', weaknesses: '', habits: '',
    appearance: '', signatureItems: '', relationships: '',
  };
}
const uploadCharacterDraft = ref<CharacterEditDraft>(createEmptyCharacterDraft());
const profileEditing = ref(false);
const characterDraft = ref<CharacterEditDraft | null>(null);
const characterImageInput = ref<HTMLInputElement>();
const characterImageAddInput = ref<HTMLInputElement>();
interface CharacterGalleryDraftItem {
  key: string;
  src: string;
  originalSrc?: string;
  file?: File;
}
const characterGalleryDraft = ref<CharacterGalleryDraftItem[]>([]);
const selectedCharacterImage = ref('');
const characterImageActionConfirm = ref<{ type: 'replace' | 'delete'; targetKey: string } | null>(null);
const profileSaveNotice = ref('');
const assetEditing = ref(false);
const assetDraft = ref({ title: '', category: '', status: '', description: '' });
const deleteConfirmOpen = ref(false);
const imagePreviewOpen = ref(false);
const imagePreviewZoomed = ref(false);

const promptTemplates = ref<PromptTemplate[]>([
  {
    id: 1,
    title: '写实角色定妆照',
    category: '人物角色',
    model: 'Midjourney / 即梦',
    description: '用于生成统一、可复用的影视角色肖像，适合人物建档与选角沟通。',
    content: '[角色姓名]，[年龄]岁，[职业/身份]。面部特征：[脸型、五官、发型与肤色]；性格气质：[克制、果断、敏锐]；服装：[服装款式、材质与颜色]。正面半身定妆照，直视镜头，神态自然，真实皮肤纹理，电影级柔光，背景简洁，中性灰色调，85mm 人像镜头，浅景深，高细节，保持现实主义质感，避免过度磨皮和夸张表情。',
    tags: ['定妆照', '写实人物', '角色一致性'],
    uses: 86,
    updated: '今天 14:20',
  },
  {
    id: 2,
    title: '角色标准三视图',
    category: '人物角色',
    model: '通用图像生成',
    description: '生成正面、侧面和背面的全身设定，方便后续镜头保持角色一致。',
    content: '同一人物角色的标准三视图设定：正面、左侧面、背面，全身站姿，三幅画面并排且比例一致。角色为[年龄与身份]，身高体型[填写体态]，穿着[服装完整描述]，发型和配饰保持完全一致。纯浅灰背景，均匀棚拍光，无透视夸张，无动作遮挡，脚部完整入镜，写实影视人物设定图，高分辨率。',
    tags: ['三视图', '人物设定', '一致性'],
    uses: 64,
    updated: '昨天 18:05',
  },
  {
    id: 3,
    title: '都市写字楼清晨空镜',
    category: '场景空间',
    model: 'Midjourney',
    description: '建立职场题材的空间基调，可作为片头、转场或人物入场镜头。',
    content: '清晨七点的现代都市甲级写字楼大堂，落地玻璃外有微冷的晨光，室内暖色灯尚未完全关闭。前台无人，电梯数字缓慢跳动，抛光石材地面映出轻微倒影，空气安静而克制。广角建立镜头，低机位轻微仰拍，构图对称，现实主义职场电影质感，低饱和青灰与暖金对比，细节真实，无文字、无品牌标识。',
    tags: ['写字楼', '空镜', '建立镜头'],
    uses: 52,
    updated: '9月2日',
  },
  {
    id: 4,
    title: '双人对话分镜拆解',
    category: '分镜画面',
    model: '通用脚本模型',
    description: '把一段双人对白拆成可拍摄的镜头表，兼顾轴线、节奏和情绪递进。',
    content: '请将以下双人对话拆解为影视分镜表：[粘贴对白]。场景信息：[地点、时间、人物关系]。输出字段必须包括：镜号、景别、机位、镜头运动、人物动作、对白、情绪变化、预计时长、声音设计。遵守180度轴线原则；先用双人中景建立空间，再用过肩镜头推进冲突，在关键沉默处插入手部或物件特写。总时长控制在[时长]秒，避免为每一句对白机械切镜。',
    tags: ['对白戏', '分镜表', '节奏控制'],
    uses: 103,
    updated: '9月1日',
  },
  {
    id: 5,
    title: '人物入场推进镜头',
    category: '视频运镜',
    model: 'Runway / 可灵',
    description: '用于人物首次出现或重要登场，突出环境压力与角色气场。',
    content: '镜头从[场景前景物]后方缓慢滑出，先呈现环境，再发现[角色姓名]从画面深处向镜头走来。摄影机以稳定器低速后退，始终保持人物上半身为视觉中心；背景人群自然流动但不抢焦。角色步伐[节奏描述]，表情[情绪描述]，衣物与发丝运动符合物理规律。镜头时长6秒，24fps，轻微手持呼吸感，电影级自然光，禁止面部变形、肢体闪烁和突然变焦。',
    tags: ['推进', '人物入场', '图生视频'],
    uses: 71,
    updated: '8月30日',
  },
  {
    id: 6,
    title: '现实主义职场电影质感',
    category: '视觉风格',
    model: '全平台通用',
    description: '统一整季画面的色彩、光线与材质表现，可附加到人物或场景提示词末尾。',
    content: '现实主义职场剧情片视觉风格：低饱和青灰主色，局部暖色实用光源，真实室内混合色温；中等对比度，保留暗部细节，肤色自然不过度美化；构图克制，使用35mm与50mm电影镜头，浅景深仅用于情绪重点；玻璃、金属、织物材质细节可信，轻微胶片颗粒，画面安静、理性且有隐约压力。避免商业广告光、赛博霓虹、过度锐化和塑料皮肤。',
    tags: ['风格统一', '职场', '电影感'],
    uses: 129,
    updated: '8月28日',
  },
]);
const promptFilter = ref('全部');
const promptSearch = ref('');
const selectedPrompt = ref<PromptTemplate | null>(null);
type PromptDialogMode = 'view' | 'edit' | 'create';
interface PromptDraft {
  title: string;
  category: string;
  model: string;
  description: string;
  content: string;
  tags: string;
}
const promptDialogMode = ref<PromptDialogMode>('view');
const promptDraft = ref<PromptDraft>({ title: '', category: '人物角色', model: '', description: '', content: '', tags: '' });
const promptFileInput = ref<HTMLInputElement>();
const promptSaveNotice = ref('');

const navItems = [
  { label: '工作台', icon: markRaw(LayoutDashboard), target: 'dashboard' as View },
  { label: '剧本中心', icon: markRaw(BookOpenText), target: 'scripts' as View },
  { label: '分镜中心', icon: markRaw(Clapperboard), target: 'assets' as View, filter: '分镜' },
  { label: '角色人物', icon: markRaw(Users), target: 'assets' as View, filter: '人物' },
  { label: '场景素材', icon: markRaw(Image), target: 'assets' as View, filter: '场景' },
  { label: '服装道具', icon: markRaw(Sparkles), target: 'assets' as View, filter: '服装道具' },
  { label: '提示词库', icon: markRaw(FileText), target: 'prompts' as View },
  { label: '视频与音频', icon: markRaw(Film), target: 'assets' as View, filter: '视频音频' },
];

const selectedEpisode = computed(() => episodes.value.find((item) => item.id === selectedEpisodeId.value) ?? episodes.value[0]);
const filteredAssets = computed(() => assetFilter.value === '全部' ? assets.value : assets.value.filter((item) => item.category === assetFilter.value));
const promptCategories = computed(() => ['全部', ...new Set(promptTemplates.value.map((item) => item.category))]);
const filteredPrompts = computed(() => {
  const keyword = promptSearch.value.trim().toLocaleLowerCase();
  return promptTemplates.value.filter((item) => {
    const matchesCategory = promptFilter.value === '全部' || item.category === promptFilter.value;
    const haystack = [item.title, item.category, item.model, item.description, item.content, ...item.tags].join(' ').toLocaleLowerCase();
    return matchesCategory && (!keyword || haystack.includes(keyword));
  });
});
const uploadContentCopy = computed(() => ({
  人物: { label: '人物设定', placeholder: '例如：角色身份、年龄、性格或造型要求' },
  场景: { label: '场景说明', placeholder: '例如：地点、时间、氛围或镜头用途' },
  分镜: { label: '分镜说明', placeholder: '例如：对应集数、镜号或画面动作' },
  服装道具: { label: '服装道具说明', placeholder: '例如：使用角色、年代、材质或搭配要求' },
  视频音频: { label: '视听说明', placeholder: '例如：时长、场次、版权或使用方式' },
}[uploadDraft.value.category] ?? { label: '素材说明', placeholder: '补充这份素材的用途或备注' }));
const canManageSelectedAsset = computed(() => selectedAsset.value ? canManageAsset(selectedAsset.value) : false);
function isGalleryCategory(category: string) {
  return ['人物', '场景', '分镜', '服装道具', '视频音频'].includes(category);
}
function acceptsGalleryFile(file: File, category: string) {
  return category === '视频音频' ? /^(video|audio)\//.test(file.type) : file.type.startsWith('image/');
}
function assetImages(asset: Asset | null) {
  if (!asset) return [];
  const sources = asset.category === '视频音频' ? (asset.images ?? []).filter((source) => source !== '/assets/hunktiger-logo.png') : [...(asset.images ?? []), asset.image];
  const uniqueSources = sources.filter((source, index, list) => source && list.indexOf(source) === index);
  return uniqueSources.length ? uniqueSources : [asset.image];
}
const galleryEditing = computed(() => profileEditing.value || (assetEditing.value && isGalleryCategory(selectedAsset.value?.category ?? '')));
const characterImages = computed(() => galleryEditing.value
  ? characterGalleryDraft.value.map((item) => item.src)
  : assetImages(selectedAsset.value));
const characterImageSrc = computed(() => selectedCharacterImage.value || characterImages.value[0] || '/assets/hunktiger-logo.png');
const characterGalleryChanged = computed(() => {
  if (!galleryEditing.value) return false;
  const originals = assetImages(selectedAsset.value);
  const retainedOriginals = characterGalleryDraft.value.map((item) => item.originalSrc).filter(Boolean);
  return characterGalleryDraft.value.some((item) => item.file) || retainedOriginals.length !== originals.length;
});
const selectedCharacterImageIndex = computed(() => characterImages.value.indexOf(characterImageSrc.value));
function galleryMediaKind(source: string) {
  const stagedFile = characterGalleryDraft.value.find((item) => item.src === source)?.file;
  const mimeType = stagedFile?.type ?? '';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  const path = source.split(/[?#]/)[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v|avi|mkv|mpeg|mpg|ogv|3gp)$/.test(path)) return 'video';
  if (/\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|wma|weba)$/.test(path)) return 'audio';
  return 'image';
}
function assetDisplayMediaKind(asset: Asset) {
  return galleryMediaKind(asset.image);
}
const selectedGalleryMediaKind = computed(() => galleryMediaKind(characterImageSrc.value));
const characterImageActionTarget = computed(() => {
  const action = characterImageActionConfirm.value;
  return action ? characterGalleryDraft.value.find((item) => item.key === action.targetKey) ?? null : null;
});
const characterImageActionTargetIndex = computed(() => {
  const target = characterImageActionTarget.value;
  return target ? characterGalleryDraft.value.findIndex((item) => item.key === target.key) : -1;
});
const activeCharacterProfile = computed<CharacterProfile | null>(() => {
  const asset = selectedAsset.value;
  if (!asset || asset.category !== '人物') return null;
  return asset.profile ?? characterProfileFallbacks[asset.id] ?? {
    name: asset.title.split('｜')[0], englishName: 'Character Profile', role: '待补充角色定位', age: 0,
    birthday: '待补充', zodiac: '待补充', height: '待补充', hometown: '待补充', education: '待补充', occupation: '待补充',
    quote: '这个角色的核心表达尚待创作团队补充。', bio: '人物小传尚待补充。',
    personality: ['性格特征待补充'], talents: ['职业天赋待补充'], weaknesses: ['人物弱点待补充'], habits: ['行为习惯待补充'],
    appearance: ['形象设定待补充'], signatureItems: ['标志物待补充'], relationships: ['人物关系待补充'],
  };
});
const imagePreviewSrc = computed(() => selectedAsset.value && isGalleryCategory(selectedAsset.value.category) ? characterImageSrc.value : selectedAsset.value?.image ?? '');
const scriptContent = computed(() => {
  const episode = selectedEpisode.value;
  if (!episode) return '';
  if (scriptMode.value === 'current') return episode.current;
  if (scriptMode.value === 'previous') return episode.previous;
  return episode.draft;
});
const pendingReviews = computed(() => episodes.value.filter((item) => item.reviewReady));
const reviewEpisode = computed(() => episodes.value.find((item) => item.id === reviewEpisodeId.value) ?? pendingReviews.value[0] ?? episodes.value[0]);
const stats = computed(() => [
  { label: '本季剧集', value: episodes.value.length, suffix: '集', hint: `${episodes.value.filter((item) => item.version > 0).length} 集已有版本`, icon: markRaw(BookOpenText) },
  { label: '待审核内容', value: episodes.value.filter((item) => item.reviewReady).length + assets.value.filter((item) => item.status === '待确认').length, suffix: '项', hint: '需要你的决定', icon: markRaw(Clock3) },
  { label: '数字资产', value: assets.value.length, suffix: '项', hint: `${assets.value.filter((item) => item.status === '已锁定').length} 项已锁定`, icon: markRaw(FolderOpen) },
  { label: '协作成员', value: members.value.length, suffix: '人', hint: `${members.value.filter((item) => item.status === '已启用').length} 人已启用`, icon: markRaw(Users) },
]);

function notify(message: string) {
  toast.value = message;
  window.setTimeout(() => { if (toast.value === message) toast.value = ''; }, 2600);
}

function showProfileSaveNotice(message: string) {
  profileSaveNotice.value = message;
  window.setTimeout(() => {
    if (profileSaveNotice.value === message) profileSaveNotice.value = '';
  }, 2800);
}

function openPrompt(prompt: PromptTemplate) {
  selectedPrompt.value = prompt;
  promptDialogMode.value = 'view';
  promptSaveNotice.value = '';
  document.body.style.overflow = 'hidden';
}

function closePrompt() {
  selectedPrompt.value = null;
  promptDialogMode.value = 'view';
  promptSaveNotice.value = '';
  if (promptFileInput.value) promptFileInput.value.value = '';
  document.body.style.overflow = '';
}

function promptToDraft(prompt?: PromptTemplate): PromptDraft {
  return prompt ? {
    title: prompt.title,
    category: prompt.category,
    model: prompt.model,
    description: prompt.description,
    content: prompt.content,
    tags: prompt.tags.join('，'),
  } : { title: '', category: promptFilter.value === '全部' ? '人物角色' : promptFilter.value, model: '', description: '', content: '', tags: '' };
}

function openPromptCreate() {
  selectedPrompt.value = null;
  promptDraft.value = promptToDraft();
  promptDialogMode.value = 'create';
  promptSaveNotice.value = '';
  document.body.style.overflow = 'hidden';
}

function startPromptEdit() {
  if (!selectedPrompt.value || !canEditContent.value) return;
  promptDraft.value = promptToDraft(selectedPrompt.value);
  promptDialogMode.value = 'edit';
  promptSaveNotice.value = '';
}

function cancelPromptEdit() {
  if (promptDialogMode.value === 'create') closePrompt();
  else promptDialogMode.value = 'view';
}

function promptPayload() {
  return {
    title: promptDraft.value.title.trim(),
    category: promptDraft.value.category.trim(),
    model: promptDraft.value.model.trim(),
    description: promptDraft.value.description.trim(),
    content: promptDraft.value.content.trim(),
    tags: [...new Set(promptDraft.value.tags.split(/[，,\n#]+/).map((tag) => tag.trim()).filter(Boolean))].slice(0, 12),
  };
}

async function savePrompt() {
  if (busy.value) return;
  const payload = promptPayload();
  if (!payload.title || !payload.category || !payload.model || !payload.description || !payload.content) {
    notify('请完整填写提示词名称、分类、适用模型、用途和正文');
    return;
  }
  busy.value = true;
  const creating = promptDialogMode.value === 'create';
  try {
    const saved = creating
      ? await api.createPrompt(payload)
      : await api.updatePrompt(selectedPrompt.value!.id, payload);
    await loadData(false);
    selectedPrompt.value = promptTemplates.value.find((item) => item.id === saved.id) ?? saved;
    promptDialogMode.value = 'view';
    promptSaveNotice.value = creating ? '提示词已上传并加入团队提示词库' : '提示词修改已保存并同步到列表';
    window.setTimeout(() => {
      if (promptSaveNotice.value) promptSaveNotice.value = '';
    }, 2800);
  } catch (error) {
    notify(error instanceof Error ? error.message : '保存提示词失败');
  } finally {
    busy.value = false;
  }
}

async function importPromptFile(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  if (!/\.(txt|md|markdown)$/i.test(file.name) && !/^text\//.test(file.type)) {
    notify('请选择 TXT 或 Markdown 提示词文件');
    input.value = '';
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    notify('提示词文件不能超过 2MB');
    input.value = '';
    return;
  }
  const importedContent = await file.text();
  promptDraft.value.content = importedContent.slice(0, 20000);
  if (!promptDraft.value.title.trim()) promptDraft.value.title = file.name.replace(/\.(txt|md|markdown)$/i, '');
  notify(importedContent.length > 20000 ? `已导入“${file.name}”，正文已保留前 20000 个字符` : `已导入“${file.name}”`);
  input.value = '';
}

async function copyPrompt(prompt: PromptTemplate) {
  try {
    await navigator.clipboard.writeText(prompt.content);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = prompt.content;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  }
  try {
    const updated = await api.recordPromptUse(prompt.id);
    const index = promptTemplates.value.findIndex((item) => item.id === updated.id);
    if (index >= 0) promptTemplates.value.splice(index, 1, updated);
    if (selectedPrompt.value?.id === updated.id) selectedPrompt.value = updated;
  } catch { /* Copy succeeded; usage statistics can retry on the next copy. */ }
  notify(`已复制“${prompt.title}”`);
}

function go(target: View, filter = '全部') {
  if (target === 'members' && !isAdmin.value) return;
  view.value = target;
  mobileMenu.value = false;
  if (target === 'assets') assetFilter.value = filter;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function chooseEpisode(episode: Episode) {
  selectedEpisodeId.value = episode.id;
  scriptMode.value = 'draft';
  editing.value = false;
  draftBuffer.value = episode.draft;
}

async function run(action: () => Promise<unknown>, success: string) {
  if (busy.value) return;
  busy.value = true;
  try {
    await action();
    await loadData(false);
    notify(success);
  } catch (error) {
    notify(error instanceof Error ? error.message : '操作失败，请稍后重试');
  } finally {
    busy.value = false;
  }
}

async function loadData(showLoader = true) {
  if (showLoader) loading.value = true;
  try {
    const data: BootstrapData = await api.bootstrap();
    episodes.value = data.episodes;
    assets.value = data.assets;
    promptTemplates.value = data.prompts ?? promptTemplates.value;
    members.value = data.members;
    activities.value = data.activities;
    const episode = data.episodes.find((item) => item.id === selectedEpisodeId.value) ?? data.episodes[0];
    if (episode && !editing.value) draftBuffer.value = episode.draft;
    loadError.value = '';
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '无法连接服务';
  } finally {
    loading.value = false;
  }
}

function saveDraft() {
  const episode = selectedEpisode.value;
  if (!episode) return;
  run(() => api.saveDraft(episode.id, draftBuffer.value), '草稿已保存');
  editing.value = false;
}

function submitReview() {
  const episode = selectedEpisode.value;
  if (episode) run(() => api.submitReview(episode.id), `${episode.code} 已提交审核`);
}

function publishEpisode() {
  const episode = selectedEpisode.value;
  if (episode) run(() => api.publish(episode.id), `已发布为 V${episode.version + 1}`);
  scriptMode.value = 'current';
}

async function publishReview(episode: Episode) {
  await run(() => api.publish(episode.id), `${episode.code} 已审核并发布为 V${episode.version + 1}`);
  const nextPending = pendingReviews.value[0];
  if (nextPending) reviewEpisodeId.value = nextPending.id;
}

function openEpisodeInScriptRoom(episode: Episode) {
  selectedEpisodeId.value = episode.id;
  scriptMode.value = 'draft';
  go('scripts');
}

function restoreEpisode() {
  const episode = selectedEpisode.value;
  if (episode) run(() => api.restore(episode.id), '已恢复前一正式版本');
  scriptMode.value = 'current';
}

async function cycleAsset(asset: Asset) {
  if (!canManageAsset(asset)) return notify('只能操作自己上传的素材');
  const statuses = ['修改中', '待确认', '已锁定'];
  const next = statuses[(statuses.indexOf(asset.status) + 1) % statuses.length];
  await run(() => api.updateAsset(asset.id, next), `${asset.title} 已改为“${next}”`);
  if (selectedAsset.value?.id === asset.id) {
    selectedAsset.value = assets.value.find((item) => item.id === asset.id) ?? null;
  }
}

function canManageAsset(asset: Asset) {
  return currentUser.value?.role === '管理员' || asset.uploaderId === currentUser.value?.id;
}

function openAsset(asset: Asset) {
  clearCharacterImageDraft();
  selectedAsset.value = asset;
  const gallery = assetImages(asset);
  selectedCharacterImage.value = gallery.includes(asset.image) ? asset.image : gallery[0] || asset.image;
  profileSaveNotice.value = '';
  profileEditing.value = false;
  assetEditing.value = false;
  deleteConfirmOpen.value = false;
  imagePreviewOpen.value = false;
  imagePreviewZoomed.value = false;
  characterDraft.value = null;
  document.body.style.overflow = 'hidden';
}

function closeAsset() {
  clearCharacterImageDraft();
  selectedAsset.value = null;
  selectedCharacterImage.value = '';
  profileSaveNotice.value = '';
  profileEditing.value = false;
  assetEditing.value = false;
  deleteConfirmOpen.value = false;
  imagePreviewOpen.value = false;
  imagePreviewZoomed.value = false;
  characterDraft.value = null;
  document.body.style.overflow = '';
}

function openImagePreview() {
  imagePreviewZoomed.value = false;
  imagePreviewOpen.value = true;
}

function closeImagePreview() {
  imagePreviewOpen.value = false;
  imagePreviewZoomed.value = false;
}

function downloadSelectedImage() {
  if (!selectedAsset.value || !characterImageSrc.value) return;
  const galleryItem = characterGalleryDraft.value.find((item) => item.src === characterImageSrc.value);
  const sourceName = galleryItem?.file?.name || characterImageSrc.value.split('/').pop() || 'asset-image.jpg';
  const extension = sourceName.match(/\.[a-zA-Z0-9]{1,5}(?:\?|$)/)?.[0]?.replace('?', '') || '.jpg';
  const safeName = (activeCharacterProfile.value?.name || selectedAsset.value.title).replace(/[\\/:*?"<>|]/g, '-');
  const itemLabel = selectedAsset.value.category === '视频音频' ? '文件' : '图片';
  const link = document.createElement('a');
  link.href = characterImageSrc.value;
  link.download = `${safeName}-${itemLabel}-${selectedCharacterImageIndex.value + 1}${extension}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function setSelectedAsDisplayImage() {
  const asset = selectedAsset.value;
  const image = characterImageSrc.value;
  if (!asset || !image || !canManageSelectedAsset.value || image === asset.image || busy.value) return;
  if (image.startsWith('blob:')) {
    notify('请先保存新添加或更换的图片，再设为展示图');
    return;
  }
  const selectedIndex = assetImages(asset).indexOf(image);
  busy.value = true;
  try {
    const updatedAsset = await api.setAssetDisplayImage(asset.id, image);
    assets.value = assets.value.map((item) => item.id === updatedAsset.id ? updatedAsset : item);
    selectedAsset.value = updatedAsset;
    selectedCharacterImage.value = updatedAsset.image;
    const itemLabel = updatedAsset.category === '视频音频' ? '个文件' : '张图片';
    notify(`已将第 ${selectedIndex + 1} ${itemLabel}设为“${updatedAsset.title}”的展示图`);
  } catch (error) {
    notify(error instanceof Error ? error.message : '设置展示图失败');
  } finally {
    busy.value = false;
  }
}

function startProfileEdit() {
  if (!activeCharacterProfile.value || !canManageSelectedAsset.value) return;
  profileSaveNotice.value = '';
  clearCharacterImageDraft();
  const profile = activeCharacterProfile.value;
  characterGalleryDraft.value = assetImages(selectedAsset.value).map((src, index) => ({ key: `saved-${index}-${src}`, src, originalSrc: src }));
  selectedCharacterImage.value = selectedAsset.value?.image || characterGalleryDraft.value[0]?.src || '';
  characterDraft.value = {
    ...profile,
    personality: profile.personality.join('\n'),
    talents: profile.talents.join('\n'),
    weaknesses: profile.weaknesses.join('\n'),
    habits: profile.habits.join('\n'),
    appearance: (profile.appearance ?? []).join('\n'),
    signatureItems: (profile.signatureItems ?? []).join('\n'),
    relationships: (profile.relationships ?? []).join('\n'),
  };
  profileEditing.value = true;
}

function startAssetEdit() {
  if (!selectedAsset.value || !canManageSelectedAsset.value) return;
  profileSaveNotice.value = '';
  clearCharacterImageDraft();
  if (isGalleryCategory(selectedAsset.value.category)) {
    characterGalleryDraft.value = assetImages(selectedAsset.value).map((src, index) => ({ key: `saved-${index}-${src}`, src, originalSrc: src }));
    selectedCharacterImage.value = characterGalleryDraft.value[0]?.src ?? '';
  }
  assetDraft.value = {
    title: selectedAsset.value.title,
    category: selectedAsset.value.category,
    status: selectedAsset.value.status,
    description: selectedAsset.value.description ?? '',
  };
  assetEditing.value = true;
}

function cancelAssetEdit() {
  clearCharacterImageDraft();
  assetEditing.value = false;
  selectedCharacterImage.value = assetImages(selectedAsset.value)[0] ?? '';
}

function pendingGalleryChanges() {
  if (!selectedAsset.value || !isGalleryCategory(selectedAsset.value.category)) {
    return { deletedImages: [] as string[], replacedImages: [] as CharacterGalleryDraftItem[], addedImages: [] as CharacterGalleryDraftItem[], count: 0 };
  }
  const originalImages = assetImages(selectedAsset.value);
  const galleryDraft = [...characterGalleryDraft.value];
  const retainedOriginals = new Set(galleryDraft.map((item) => item.originalSrc).filter((image): image is string => Boolean(image)));
  const deletedImages = originalImages.filter((image) => !retainedOriginals.has(image));
  const replacedImages = galleryDraft.filter((item) => item.file && item.originalSrc);
  const addedImages = galleryDraft.filter((item) => item.file && !item.originalSrc);
  return { deletedImages, replacedImages, addedImages, count: deletedImages.length + replacedImages.length + addedImages.length };
}

async function persistGalleryChanges(assetId: number, changes: ReturnType<typeof pendingGalleryChanges>) {
  for (const item of changes.replacedImages) await api.replaceAssetGalleryImage(assetId, item.originalSrc!, item.file!);
  for (const image of changes.deletedImages) await api.deleteAssetImage(assetId, image);
  for (const item of changes.addedImages) await api.addAssetImage(assetId, item.file!);
}

async function saveAssetDetails() {
  if (!selectedAsset.value || !assetDraft.value.title.trim() || busy.value) return;
  busy.value = true;
  const assetId = selectedAsset.value.id;
  const imageChanges = pendingGalleryChanges();
  try {
    await persistGalleryChanges(assetId, imageChanges);
    await api.updateAssetDetails(assetId, { ...assetDraft.value, title: assetDraft.value.title.trim() });
    await loadData(false);
    selectedAsset.value = assets.value.find((item) => item.id === assetId) ?? null;
    selectedCharacterImage.value = assetImages(selectedAsset.value)[0] ?? '';
    cancelAssetEdit();
    const itemLabel = selectedAsset.value?.category === '视频音频' ? '个文件' : '张图片';
    showProfileSaveNotice(imageChanges.count ? `素材资料已保存，并同步更新 ${imageChanges.count} ${itemLabel}` : '素材资料已保存并同步更新');
  } catch (error) {
    notify(error instanceof Error ? error.message : '保存素材失败');
  } finally {
    busy.value = false;
  }
}

function requestAssetDelete() {
  if (!selectedAsset.value || !canManageSelectedAsset.value) return;
  deleteConfirmOpen.value = true;
}

async function confirmAssetDelete() {
  if (!selectedAsset.value || busy.value) return;
  busy.value = true;
  const title = selectedAsset.value.title;
  try {
    await api.deleteAsset(selectedAsset.value.id);
    closeAsset();
    await loadData(false);
    notify(`素材“${title}”已删除`);
  } catch (error) {
    notify(error instanceof Error ? error.message : '删除素材失败');
  } finally {
    busy.value = false;
  }
}

function handleAssetImageError(event: Event) {
  const image = event.currentTarget as HTMLImageElement;
  if (image.dataset.fallbackApplied) return;
  image.dataset.fallbackApplied = 'true';
  image.src = '/assets/hunktiger-logo.png';
  image.classList.add('contain', 'image-fallback');
}

function cancelProfileEdit() {
  clearCharacterImageDraft();
  profileEditing.value = false;
  characterDraft.value = null;
  selectedCharacterImage.value = selectedAsset.value?.image ?? '';
}

function clearCharacterImageDraft() {
  for (const item of characterGalleryDraft.value) {
    if (item.src.startsWith('blob:')) URL.revokeObjectURL(item.src);
  }
  characterGalleryDraft.value = [];
  characterImageActionConfirm.value = null;
  if (characterImageInput.value) characterImageInput.value.value = '';
  if (characterImageAddInput.value) characterImageAddInput.value.value = '';
}

function openCharacterImagePicker(mode: 'add' | 'replace') {
  if (busy.value) return;
  (mode === 'add' ? characterImageAddInput.value : characterImageInput.value)?.click();
}

function selectCharacterImage(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  const category = selectedAsset.value?.category ?? '人物';
  if (!acceptsGalleryFile(file, category)) {
    notify(category === '视频音频' ? '请选择视频或音频文件' : '请选择图片文件');
    input.value = '';
    return;
  }
  if (file.size > 80 * 1024 * 1024) {
    notify(`${file.name} 超过 80MB，请压缩后再上传`);
    input.value = '';
    return;
  }
  const selectedIndex = Math.max(0, characterGalleryDraft.value.findIndex((item) => item.src === selectedCharacterImage.value));
  const currentItem = characterGalleryDraft.value[selectedIndex];
  if (!currentItem) return;
  if (currentItem.src.startsWith('blob:')) URL.revokeObjectURL(currentItem.src);
  const preview = URL.createObjectURL(file);
  characterGalleryDraft.value.splice(selectedIndex, 1, { ...currentItem, src: preview, file });
  selectedCharacterImage.value = preview;
  input.value = '';
}

function addCharacterImages(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  const category = selectedAsset.value?.category ?? '人物';
  const invalid = files.find((file) => !acceptsGalleryFile(file, category));
  const oversized = files.find((file) => file.size > 80 * 1024 * 1024);
  if (invalid) notify(category === '视频音频' ? `${invalid.name} 不是视频或音频文件` : `${invalid.name} 不是图片文件`);
  else if (oversized) notify(`${oversized.name} 超过 80MB，请压缩后再上传`);
  else if (files.length) {
    const existingFileKeys = new Set(characterGalleryDraft.value.filter((item) => item.file).map((item) => `${item.file!.name}:${item.file!.size}:${item.file!.lastModified}`));
    const additions = files.filter((file) => !existingFileKeys.has(`${file.name}:${file.size}:${file.lastModified}`));
    const newItems = additions.map((file, index) => ({
      key: `new-${Date.now()}-${index}-${file.name}`,
      src: URL.createObjectURL(file),
      file,
    }));
    characterGalleryDraft.value.push(...newItems);
    if (newItems.length) selectedCharacterImage.value = newItems[0].src;
    if (additions.length < files.length) notify(category === '视频音频' ? '已跳过重复文件' : '已跳过重复图片');
  }
  input.value = '';
}

function requestCharacterImageAction(type: 'replace' | 'delete') {
  if (type === 'delete' && characterGalleryDraft.value.length <= 1) {
    const itemLabel = selectedAsset.value?.category === '视频音频' ? '一个文件' : '一张图片';
    notify(`素材至少需要保留${itemLabel}，可使用“更换”替换当前文件`);
    return;
  }
  const target = characterGalleryDraft.value.find((item) => item.src === selectedCharacterImage.value);
  if (!target) return;
  characterImageActionConfirm.value = { type, targetKey: target.key };
}

function confirmCharacterImageAction() {
  const action = characterImageActionConfirm.value;
  const target = characterImageActionTarget.value;
  if (!action || !target) return;
  if (action.type === 'replace') {
    selectedCharacterImage.value = target.src;
    characterImageActionConfirm.value = null;
    openCharacterImagePicker('replace');
    return;
  }
  const index = characterGalleryDraft.value.findIndex((item) => item.key === target.key);
  if (index < 0) return;
  const [removed] = characterGalleryDraft.value.splice(index, 1);
  if (removed.src.startsWith('blob:')) URL.revokeObjectURL(removed.src);
  selectedCharacterImage.value = characterGalleryDraft.value[Math.min(index, characterGalleryDraft.value.length - 1)]?.src ?? '';
  characterImageActionConfirm.value = null;
}

function splitProfileLines(value: string) {
  return value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
}

async function saveProfile() {
  if (!selectedAsset.value || !characterDraft.value || busy.value) return;
  if (!characterDraft.value.name.trim() || !characterDraft.value.bio.trim()) {
    notify('角色姓名和人物简介不能为空');
    return;
  }
  const draft = characterDraft.value;
  const profile: CharacterProfile = {
    ...draft,
    age: Number(draft.age) || 0,
    personality: splitProfileLines(draft.personality),
    talents: splitProfileLines(draft.talents),
    weaknesses: splitProfileLines(draft.weaknesses),
    habits: splitProfileLines(draft.habits),
    appearance: splitProfileLines(draft.appearance),
    signatureItems: splitProfileLines(draft.signatureItems),
    relationships: splitProfileLines(draft.relationships),
  };
  busy.value = true;
  const assetId = selectedAsset.value.id;
  const imageChanges = pendingGalleryChanges();
  try {
    await api.updateCharacter(assetId, profile);
    await persistGalleryChanges(assetId, imageChanges);
    await loadData(false);
    selectedAsset.value = assets.value.find((item) => item.id === assetId) ?? null;
    selectedCharacterImage.value = selectedAsset.value?.image ?? '';
    cancelProfileEdit();
    showProfileSaveNotice(imageChanges.count ? `角色资料已保存，并同步更新 ${imageChanges.count} 张图片` : '角色资料已保存，外部卡片已同步更新');
  } catch (error) {
    notify(error instanceof Error ? error.message : '保存角色资料失败');
  } finally {
    busy.value = false;
  }
}

function handleGlobalKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  if (characterImageActionConfirm.value) {
    characterImageActionConfirm.value = null;
    return;
  }
  if (selectedPrompt.value || promptDialogMode.value === 'create') {
    closePrompt();
    return;
  }
  if (uploadNoFileConfirmOpen.value) {
    uploadNoFileConfirmOpen.value = false;
    return;
  }
  if (uploadDialogOpen.value) {
    closeUploadDialog();
    return;
  }
  if (imagePreviewOpen.value) {
    closeImagePreview();
    return;
  }
  if (selectedAsset.value) closeAsset();
  showUserMenu.value = false;
}

async function login() {
  if (loginBusy.value) return;
  loginError.value = '';
  if (!loginEmail.value.trim() || !loginPassword.value) {
    loginError.value = '请输入邮箱和密码';
    return;
  }
  loginBusy.value = true;
  try {
    const email = loginEmail.value.trim();
    const result = await api.login(email, loginPassword.value, rememberLogin.value);
    storeToken(result.token, rememberLogin.value);
    storeLoginEmail(result.user.email || email);
    loginEmail.value = result.user.email || email;
    currentUser.value = result.user;
    view.value = 'dashboard';
    await loadData();
  } catch (error) {
    clearStoredToken();
    loginError.value = error instanceof Error ? error.message : '登录失败，请稍后重试';
  } finally {
    loginBusy.value = false;
  }
}

async function logout() {
  try { await api.logout(); } catch { /* The local session still needs to end. */ }
  clearStoredToken();
  currentUser.value = null;
  loginEmail.value = getStoredLoginEmail();
  loginPassword.value = '';
  showUserMenu.value = false;
  view.value = 'dashboard';
  clearCharacterImageDraft();
  selectedAsset.value = null;
  uploadDialogOpen.value = false;
  uploadNoFileConfirmOpen.value = false;
  document.body.style.overflow = '';
}

async function restoreSession() {
  if (!getStoredToken()) {
    loading.value = false;
    return;
  }
  try {
    const result = await api.session();
    storeLoginEmail(result.user.email);
    loginEmail.value = result.user.email;
    currentUser.value = result.user;
    await loadData(false);
  } catch {
    clearStoredToken();
  } finally {
    loading.value = false;
  }
}

async function uploadFiles(event: Event) {
  const input = event.target as HTMLInputElement;
  setUploadFiles(Array.from(input.files ?? []));
  input.value = '';
}

function setUploadFiles(files: File[]) {
  if (!files.length) return;
  if (isGalleryCategory(uploadDraft.value.category) && files.some((file) => !acceptsGalleryFile(file, uploadDraft.value.category))) {
    notify(uploadDraft.value.category === '视频音频' ? '请选择视频或音频文件' : `${uploadDraft.value.category}上传只支持图片，请重新选择`);
    if (fileInput.value) fileInput.value.value = '';
    return;
  }
  const oversized = files.find((file) => file.size > 80 * 1024 * 1024);
  if (oversized) {
    notify(`${oversized.name} 超过 80MB，请压缩后再上传`);
    if (fileInput.value) fileInput.value.value = '';
    return;
  }
  if (isGalleryCategory(uploadDraft.value.category)) {
    const fileKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;
    const existingKeys = new Set(uploadFilesDraft.value.map(fileKey));
    const newFiles = files.filter((file) => !existingKeys.has(fileKey(file)));
    uploadFilesDraft.value = [...uploadFilesDraft.value, ...newFiles];
    if (!newFiles.length) notify('所选图片已经添加过了');
    else if (newFiles.length < files.length) notify('已跳过重复图片');
    return;
  }
  uploadFilesDraft.value = files;
}

function handleUploadDrop(event: DragEvent) {
  uploadDragging.value = false;
  setUploadFiles(Array.from(event.dataTransfer?.files ?? []));
}

function removeUploadFile(index: number) {
  uploadFilesDraft.value.splice(index, 1);
  if (!uploadFilesDraft.value.length && fileInput.value) fileInput.value.value = '';
}

function formatFileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function hasUploadCharacterDetails() {
  return Object.entries(uploadCharacterDraft.value).some(([key, value]) => (
    key === 'age' ? Number(value) > 0 : String(value).trim().length > 0
  ));
}

function buildUploadCharacterProfile(fallbackTitle: string): CharacterProfile {
  const draft = uploadCharacterDraft.value;
  const listOrFallback = (value: string) => {
    const items = splitProfileLines(value);
    return items.length ? items : ['待补充'];
  };
  return {
    name: draft.name.trim() || fallbackTitle.split('｜')[0],
    englishName: draft.englishName.trim() || 'Character Profile',
    role: draft.role.trim() || '待补充角色定位',
    age: Number(draft.age) || 0,
    birthday: draft.birthday.trim() || '待补充',
    zodiac: draft.zodiac.trim() || '待补充',
    height: draft.height.trim() || '待补充',
    hometown: draft.hometown.trim() || '待补充',
    education: draft.education.trim() || '待补充',
    occupation: draft.occupation.trim() || '待补充',
    quote: draft.quote.trim() || '人物金句待补充。',
    bio: draft.bio.trim() || '人物小传待补充。',
    personality: listOrFallback(draft.personality),
    talents: listOrFallback(draft.talents),
    weaknesses: listOrFallback(draft.weaknesses),
    habits: listOrFallback(draft.habits),
    appearance: listOrFallback(draft.appearance),
    signatureItems: listOrFallback(draft.signatureItems),
    relationships: listOrFallback(draft.relationships),
  };
}

function openUploadDialog() {
  if (uploading.value) return;
  uploadDraft.value = {
    category: assetFilter.value === '全部' ? '人物' : assetFilter.value,
    title: '',
    description: '',
  };
  uploadFilesDraft.value = [];
  uploadCharacterDraft.value = createEmptyCharacterDraft();
  uploadDragging.value = false;
  uploadNoFileConfirmOpen.value = false;
  if (fileInput.value) fileInput.value.value = '';
  uploadDialogOpen.value = true;
  document.body.style.overflow = 'hidden';
}

function closeUploadDialog() {
  if (uploading.value) return;
  uploadDialogOpen.value = false;
  uploadNoFileConfirmOpen.value = false;
  uploadFilesDraft.value = [];
  uploadDragging.value = false;
  if (fileInput.value) fileInput.value.value = '';
  document.body.style.overflow = selectedAsset.value ? 'hidden' : '';
}

async function submitUpload(continueWithoutFile = false) {
  const files = uploadFilesDraft.value;
  if (uploading.value) return;
  if (!files.length && !continueWithoutFile) {
    uploadNoFileConfirmOpen.value = true;
    return;
  }
  const category = uploadDraft.value.category;
  if (isGalleryCategory(category) && files.some((file) => !acceptsGalleryFile(file, category))) {
    notify(category === '视频音频' ? '视频音频分类只支持视频或音频文件' : `${category}上传只支持图片，请移除其他文件后重试`);
    return;
  }
  uploadNoFileConfirmOpen.value = false;
  uploading.value = true;
  try {
    const description = category === '人物' ? uploadCharacterDraft.value.bio : uploadDraft.value.description;
    if (!files.length) {
      uploadLabel.value = '正在创建';
      const assetTitle = category === '人物'
        ? uploadCharacterDraft.value.name.trim() || '未命名人物'
        : uploadDraft.value.title.trim() || `未命名${category}素材`;
      const uploadedAsset = await api.createAsset({ title: assetTitle, category, description });
      if (category === '人物' && hasUploadCharacterDetails()) {
        await api.updateCharacter(uploadedAsset.id, buildUploadCharacterProfile(assetTitle));
      }
    } else if (isGalleryCategory(category)) {
      const firstFileTitle = files[0].name.replace(/\.[^.]+$/, '');
      const assetTitle = category === '人物'
        ? uploadCharacterDraft.value.name.trim() || firstFileTitle
        : uploadDraft.value.title.trim() || firstFileTitle;
      uploadLabel.value = `上传中 1/${files.length}`;
      const uploadedAsset = await api.uploadAsset(files[0], category, { title: assetTitle, description });
      for (let index = 1; index < files.length; index += 1) {
        uploadLabel.value = `上传中 ${index + 1}/${files.length}`;
        await api.addAssetImage(uploadedAsset.id, files[index]);
      }
      if (hasUploadCharacterDetails()) {
        await api.updateCharacter(uploadedAsset.id, buildUploadCharacterProfile(assetTitle));
      }
    } else {
      for (let index = 0; index < files.length; index += 1) {
        uploadLabel.value = `上传中 ${index + 1}/${files.length}`;
        const fileTitle = files[index].name.replace(/\.[^.]+$/, '');
        const assetTitle = files.length === 1 ? uploadDraft.value.title.trim() || fileTitle : fileTitle;
        const uploadedAsset = await api.uploadAsset(files[index], category, {
          title: assetTitle,
          description,
        });
        if (category === '人物' && hasUploadCharacterDetails()) {
          await api.updateCharacter(uploadedAsset.id, buildUploadCharacterProfile(assetTitle));
        }
      }
    }
    await loadData(false);
    uploadDialogOpen.value = false;
    uploadFilesDraft.value = [];
    document.body.style.overflow = '';
    notify(files.length ? (isGalleryCategory(category) ? `已创建 1 项${category}素材并添加 ${files.length} 个${category === '视频音频' ? '文件' : '图片'}` : `已加入 ${files.length} 项素材`) : '已创建无图片素材');
  } catch (error) {
    notify(error instanceof Error ? error.message : '上传失败');
  } finally {
    uploading.value = false;
    uploadLabel.value = '';
  }
  if (fileInput.value) fileInput.value.value = '';
}

function openFilePicker() {
  if (uploading.value) return;
  fileInput.value?.click();
}

function invite() {
  if (!inviteEmail.value.trim()) return notify('请输入成员邮箱');
  const email = inviteEmail.value.trim();
  run(() => api.inviteMember(email, inviteRole.value), `邀请已发送给 ${email}`);
  inviteEmail.value = '';
}

function updateMember(member: Member, payload: { role?: MemberRole; status?: string }) {
  run(() => api.updateMember(member.id, payload), `${member.name} 的权限已更新`);
}

function statusClass(status: string) {
  if (status.includes('锁定') || status.includes('启用') || status.includes('发布')) return 'status status--green';
  if (status.includes('待')) return 'status status--gold';
  if (status.includes('修改') || status.includes('草稿') || status.includes('停用')) return 'status status--gray';
  return 'status';
}

function rolePermission(role: MemberRole) {
  return { 管理员: '全部权限', 编辑人员: '修改草稿、上传素材', 审阅人员: '查看草稿、评论', 查看人员: '仅查看和授权下载' }[role];
}

onMounted(() => {
  restoreSession();
  window.addEventListener('keydown', handleGlobalKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalKeydown);
  clearCharacterImageDraft();
  document.body.style.overflow = '';
});
</script>

<template>
  <div v-if="loading" class="loading-screen">
    <img src="/assets/hunktiger-logo.png" alt="HunkTiger" />
    <RefreshCw :size="22" class="spin" />
    <p>正在进入创作空间</p>
  </div>

  <main v-else-if="!currentUser" class="login-screen">
    <section class="login-story" aria-label="HunkTiger 创作空间介绍">
      <div class="login-brand"><span class="brand-mark">HT</span><div><strong>HUNK TIGER</strong><small>CREATIVE STUDIO</small></div></div>
      <div class="story-copy"><p class="story-index">01 / PRIVATE STUDIO</p><h1>让每一个好故事，<br />都有迹可循。</h1><p>剧本、角色、分镜与制作素材，汇聚在一个只属于创作团队的安全空间。</p></div>
      <div class="story-footer"><span>《你好，我是猎头》</span><small>SEASON ONE · PRODUCTION IN PROGRESS</small></div>
      <i class="story-orbit orbit-one"></i><i class="story-orbit orbit-two"></i><span class="story-number">01</span>
    </section>
    <section class="login-panel">
      <form class="login-card" autocomplete="on" @submit.prevent="login">
        <header><p class="eyebrow">WELCOME BACK</p><h2>登录创作空间</h2><span>请使用管理员分配的团队账号继续</span></header>
        <label class="login-field"><span>邮箱</span><div><UserCog :size="17" /><input v-model="loginEmail" type="email" name="username" autocomplete="username" placeholder="请输入邮箱" /></div></label>
        <label class="login-field"><span>密码</span><div><LockKeyhole :size="17" /><input v-model="loginPassword" :type="showPassword ? 'text' : 'password'" name="password" autocomplete="current-password" placeholder="请输入密码" /><button type="button" :aria-label="showPassword ? '隐藏密码' : '显示密码'" @click="showPassword = !showPassword"><EyeOff v-if="showPassword" :size="16" /><Eye v-else :size="16" /></button></div></label>
        <div class="login-options"><label><input v-model="rememberLogin" type="checkbox" /><i><Check :size="11" /></i><span>保持登录</span></label><span>账号由管理员统一创建</span></div>
        <p v-if="loginError" class="login-error" role="alert">{{ loginError }}</p>
        <button class="login-submit" type="submit" :disabled="loginBusy"><span>{{ loginBusy ? '正在验证身份…' : '进入创作空间' }}</span><RefreshCw v-if="loginBusy" :size="16" class="spin-inline" /><ChevronRight v-else :size="17" /></button>
        <footer><ShieldCheck :size="14" /><span>私有项目 · 登录会话受保护</span></footer>
      </form>
    </section>
  </main>

  <div v-else-if="loadError" class="error-screen">
    <div><p class="eyebrow">CONNECTION NOTICE</p><h1>创作空间暂时无法载入</h1><p>{{ loadError }}</p><button class="button primary" @click="loadData()"><RefreshCw :size="16" />重新连接</button></div>
  </div>

  <main v-else class="app-shell">
    <aside class="sidebar" :class="{ 'sidebar--open': mobileMenu }">
      <div class="brand"><p>HUNK TIGER</p><span>CREATIVE STUDIO</span><button class="mobile-close" aria-label="关闭菜单" @click="mobileMenu = false"><X :size="20" /></button></div>
      <div class="side-content">
        <p class="side-label">创作空间</p>
        <nav>
          <button v-for="item in navItems" :key="item.label" :class="{ active: view === item.target && (item.target !== 'assets' || assetFilter === (item.filter ?? '全部')) }" @click="go(item.target, item.filter)">
            <component :is="item.icon" :size="17" :stroke-width="1.8" />{{ item.label }}
          </button>
        </nav>
      </div>
      <div v-if="isAdmin" class="side-footer">
        <button class="admin-link" :class="{ active: view === 'members' }" @click="go('members')"><Settings :size="17" />后台管理</button>
        <button class="profile" @click="go('members')"><span class="avatar gold">{{ currentUser.initials }}</span><span><b>{{ currentUser.name }}</b><small>{{ currentUser.role }}</small></span><ChevronRight :size="14" /></button>
      </div>
    </aside>
    <div v-if="mobileMenu" class="backdrop" @click="mobileMenu = false"></div>

    <section class="main-area">
      <header class="topbar">
        <button class="menu-button" aria-label="打开菜单" @click="mobileMenu = true"><Menu :size="20" /></button>
        <label class="search"><Search :size="16" /><input placeholder="搜索剧本、人物、场景或素材" /></label>
        <button class="icon-button" aria-label="通知"><Bell :size="17" /><i></i></button>
        <button v-if="view !== 'prompts' && (view !== 'assets' || canEditContent)" class="button primary top-action" :disabled="view === 'assets' && uploading" @click="view === 'assets' ? openUploadDialog() : go('scripts')"><RefreshCw v-if="view === 'assets' && uploading" class="spin-inline" :size="16" /><Plus v-else :size="16" />{{ view === 'assets' && uploading ? uploadLabel : view === 'assets' ? '上传素材' : '新建内容' }}</button>
        <div class="user-menu-wrap">
          <button class="user-trigger" :aria-expanded="showUserMenu" aria-label="打开账号菜单" @click="showUserMenu = !showUserMenu"><span class="avatar">{{ currentUser.initials }}</span><span><b>{{ currentUser.name }}</b><small>{{ currentUser.role }}</small></span><ChevronDown :size="14" /></button>
          <div v-if="showUserMenu" class="user-popover"><div><span class="avatar gold">{{ currentUser.initials }}</span><p><b>{{ currentUser.name }}</b><small>{{ currentUser.email }}</small></p></div><button @click="logout"><LogOut :size="15" />退出登录</button></div>
        </div>
      </header>

      <section v-if="view === 'dashboard'" class="page page--wide">
        <div class="hero">
          <div><p class="eyebrow">PROJECT OVERVIEW</p><h1>《你好，我是猎头》</h1><p>第一季 · 私有影视创作协作中心</p></div>
          <button class="button primary" @click="go('scripts')"><BookOpenText :size="16" />继续创作 EP01</button>
        </div>
        <div class="stats-grid">
          <article v-for="item in stats" :key="item.label" class="stat-card"><div><p>{{ item.label }}</p><strong>{{ item.value }}<small>{{ item.suffix }}</small></strong><span>{{ item.hint }}</span></div><span class="soft-icon"><component :is="item.icon" :size="20" /></span></article>
        </div>
        <div class="dashboard-grid">
          <section class="panel focus-panel">
            <div class="panel-head"><div><p class="eyebrow">TODAY'S FOCUS</p><h2>今日重点</h2></div><button @click="go('scripts')">查看剧本 <ChevronRight :size="14" /></button></div>
            <div class="focus-body">
              <div class="episode-mark"><span>01</span><small>EPISODE</small></div>
              <div><span :class="statusClass(selectedEpisode?.status ?? '')">{{ selectedEpisode?.status }}</span><h3>{{ selectedEpisode?.code }}《{{ selectedEpisode?.title }}》</h3><p>序幕对白已更新，等待管理员完成最后审核。发布后会自动保留前一正式版本。</p><div class="progress"><i></i></div><small>剧本完成度 82%</small></div>
            </div>
            <div class="focus-actions"><button class="button primary" @click="go('scripts')">进入剧本中心</button><button class="button outline" @click="go('assets', '分镜')">查看相关分镜</button></div>
          </section>
          <section class="panel activity-panel">
            <div class="panel-head"><div><p class="eyebrow">RECENT ACTIVITY</p><h2>最近动态</h2></div><History :size="18" /></div>
            <div class="timeline"><div v-for="(activity, index) in activities.slice(0, 5)" :key="activity"><span>{{ index === 0 ? '刚刚' : `${index * 18} 分钟前` }}</span><p>{{ activity }}</p></div></div>
          </section>
        </div>
        <div class="section-heading"><div><p class="eyebrow">KEY ASSETS</p><h2>重点素材</h2></div><button @click="go('assets')">查看全部 <ChevronRight :size="14" /></button></div>
        <div class="asset-preview"><article v-for="asset in assets.slice(0, 4)" :key="asset.id" @click="go('assets', asset.category)"><video v-if="assetDisplayMediaKind(asset) === 'video'" :src="asset.image" muted preload="metadata"></video><span v-else-if="assetDisplayMediaKind(asset) === 'audio'" class="asset-preview-audio"><Film :size="28" /><small>音频素材</small></span><img v-else :src="asset.image" :alt="asset.title" /><div><span :class="statusClass(asset.status)">{{ asset.status }}</span><h3>{{ asset.title }}</h3><p>{{ asset.category }} · {{ asset.version }}</p></div></article></div>
      </section>

      <section v-if="view === 'scripts'" class="page page--xl">
        <div class="page-title"><div><p class="eyebrow">SCRIPT ROOM</p><h1>剧本中心</h1><p>一份主文档，一个编辑草稿，只保留前一正式版本</p></div><button class="button primary" @click="notify('已创建空白剧本文档')"><Plus :size="16" />新建剧本</button></div>
        <div class="script-layout">
          <aside class="episode-list panel"><div class="list-title">第一季 · {{ episodes.length }} 集</div><div><button v-for="episode in episodes" :key="episode.id" :class="{ active: episode.id === selectedEpisodeId }" @click="chooseEpisode(episode)"><small>{{ episode.code }}</small><span><b>{{ episode.title }}</b><em>{{ episode.status }} · {{ episode.version ? `V${episode.version}` : '—' }}</em></span></button></div></aside>
          <section v-if="selectedEpisode" class="script-editor panel">
            <div class="editor-head"><div><h3>{{ selectedEpisode.code }}《{{ selectedEpisode.title }}》</h3><p>当前正式版本 V{{ selectedEpisode.version }} · 约 3 分钟</p></div><div><button v-if="scriptMode === 'draft' && !editing" class="button outline small" @click="editing = true; draftBuffer = selectedEpisode.draft">开始编辑</button><button v-if="editing" class="button outline small" @click="saveDraft">保存草稿</button><button v-if="scriptMode === 'draft'" class="button primary small" @click="submitReview">提交审核</button></div></div>
            <div class="tabs"><button v-for="tab in ([['draft','编辑草稿'],['current',`正式版 V${selectedEpisode.version}`],['previous',`前一版 V${Math.max(selectedEpisode.version - 1, 1)}`],['diff','差异对比']] as [ScriptMode,string][] )" :key="tab[0]" :class="{ active: scriptMode === tab[0] }" @click="scriptMode = tab[0]; editing = false">{{ tab[1] }}</button></div>
            <div class="editor-body">
              <div v-if="scriptMode === 'diff'" class="diff"><p>对比：正式版 V{{ selectedEpisode.version }} → 待发布草稿</p><div class="removed">删除：你们猎头，不是说这个岗位直接汇报 CEO 吗？</div><div class="added">新增：你们确认过的汇报对象，是 CEO。</div><div class="added">新增：Tiger：所以别签。</div><div class="added">新增：声音直接切到二十七天前，电梯门关闭。</div></div>
              <textarea v-else-if="editing && scriptMode === 'draft'" v-model="draftBuffer" aria-label="剧本草稿"></textarea>
              <pre v-else>{{ scriptContent }}</pre>
            </div>
          </section>
          <aside v-if="selectedEpisode" class="script-side">
            <section class="panel side-card"><div class="card-title"><h3>版本状态</h3><LockKeyhole :size="16" /></div><dl><div><dt>正式版本</dt><dd>V{{ selectedEpisode.version }}</dd></div><div><dt>前一版本</dt><dd>V{{ Math.max(selectedEpisode.version - 1, 1) }}</dd></div><div><dt>草稿状态</dt><dd><span :class="statusClass(selectedEpisode.reviewReady ? '待审核' : '未提交')">{{ selectedEpisode.reviewReady ? '待审核' : '未提交' }}</span></dd></div><div><dt>编辑人员</dt><dd>{{ editing ? '你' : '任亚楠' }}</dd></div></dl><button v-if="selectedEpisode.reviewReady && isAdmin" class="button primary full" @click="publishEpisode">管理员发布为 V{{ selectedEpisode.version + 1 }}</button><button v-if="isAdmin" class="button outline full" @click="restoreEpisode"><RotateCcw :size="13" />恢复前一版本</button></section>
            <section class="panel side-card"><div class="card-title"><h3>讨论</h3><MessageSquareText :size="16" /></div><div class="comment"><b>任亚楠</b><p>开头是否需要把“27天前”再提前两秒？</p><small>18 分钟前</small></div><button class="button outline full" @click="notify('评论入口已打开')">添加评论</button></section>
            <section class="panel side-card"><h3>版本原则</h3><p class="muted">发布新版本时自动保留前一版，团队始终围绕唯一正式版本协作。</p></section>
          </aside>
        </div>
      </section>

      <section v-if="view === 'prompts'" class="page page--wide prompt-library-page">
        <div class="page-title prompt-page-title">
          <div><p class="eyebrow">PROMPT LIBRARY</p><h1>提示词库</h1><p>沉淀团队可复用的生成提示词，统一人物、场景、分镜与视频风格</p></div>
          <div class="prompt-page-actions"><button v-if="canEditContent" class="button primary" type="button" @click="openPromptCreate"><Upload :size="16" />上传提示词</button><div class="prompt-count"><FileText :size="17" /><span><strong>{{ promptTemplates.length }}</strong> 条团队模板</span></div></div>
        </div>

        <section class="prompt-overview">
          <article><span>人物与场景</span><strong>{{ promptTemplates.filter(item => ['人物角色', '场景空间'].includes(item.category)).length }}</strong><small>角色设定和空间基调</small></article>
          <article><span>分镜与运镜</span><strong>{{ promptTemplates.filter(item => ['分镜画面', '视频运镜'].includes(item.category)).length }}</strong><small>镜头设计和动态生成</small></article>
          <article><span>累计使用</span><strong>{{ promptTemplates.reduce((sum, item) => sum + item.uses, 0) }}</strong><small>团队历史调用次数</small></article>
        </section>

        <section class="panel prompt-toolbar">
          <label class="prompt-search"><Search :size="16" /><input v-model="promptSearch" type="search" placeholder="搜索提示词名称、用途、模型或标签" /></label>
          <div class="prompt-filters" aria-label="提示词分类">
            <button v-for="category in promptCategories" :key="category" :class="{ active: promptFilter === category }" @click="promptFilter = category">{{ category }}</button>
          </div>
          <span class="prompt-result-count">{{ filteredPrompts.length }} 条结果</span>
        </section>

        <div v-if="filteredPrompts.length" class="prompt-grid">
          <article v-for="prompt in filteredPrompts" :key="prompt.id" class="prompt-card" role="button" tabindex="0" :aria-label="`查看提示词：${prompt.title}`" @click="openPrompt(prompt)" @keydown.enter.self="openPrompt(prompt)" @keydown.space.self.prevent="openPrompt(prompt)">
            <header><span>{{ prompt.category }}</span><small>{{ prompt.model }}</small></header>
            <div class="prompt-card-body">
              <h2>{{ prompt.title }}</h2>
              <p>{{ prompt.description }}</p>
              <button class="prompt-preview" type="button" :aria-label="`查看完整提示词：${prompt.title}`" @click.stop="openPrompt(prompt)"><pre>{{ prompt.content }}</pre><span>{{ canEditContent ? '查看与编辑' : '查看完整内容' }} <ChevronRight :size="13" /></span></button>
              <div class="prompt-tags"><span v-for="tag in prompt.tags" :key="tag"># {{ tag }}</span></div>
            </div>
            <footer><span><Clock3 :size="13" />{{ prompt.updated }} · 已使用 {{ prompt.uses }} 次</span><button type="button" aria-label="复制提示词" @click.stop="copyPrompt(prompt)"><Copy :size="15" />复制</button></footer>
          </article>
        </div>
        <div v-else class="empty-state prompt-empty"><Search :size="28" /><h3>没有找到匹配的提示词</h3><p>换一个关键词或分类试试</p><button class="button outline" @click="promptSearch = ''; promptFilter = '全部'">清除筛选</button></div>
      </section>

      <section v-if="view === 'assets'" class="page page--wide">
        <div class="page-title"><div><p class="eyebrow">ASSET LIBRARY</p><h1>数字资产库</h1><p>人物、场景、分镜、服装道具和制作文件集中管理</p></div><button v-if="canEditContent" class="button primary" :disabled="uploading" @click="openUploadDialog"><RefreshCw v-if="uploading" class="spin-inline" :size="16" /><Upload v-else :size="16" />{{ uploading ? uploadLabel : '上传素材' }}</button></div>
        <div class="filterbar panel"><div><button v-for="filter in ['全部','人物','场景','分镜','服装道具','视频音频']" :key="filter" :class="{ active: assetFilter === filter }" @click="assetFilter = filter">{{ filter }}</button></div><span><FolderOpen :size="15" />{{ filteredAssets.length }} 项素材</span></div>
        <div v-if="filteredAssets.length" class="asset-grid"><article v-for="asset in filteredAssets" :key="asset.id" class="asset-card" role="button" tabindex="0" :aria-label="`查看素材：${asset.title}`" @click="openAsset(asset)" @keydown.enter="openAsset(asset)" @keydown.space.prevent="openAsset(asset)"><div class="asset-image"><video v-if="assetDisplayMediaKind(asset) === 'video'" :src="asset.image" muted preload="metadata"></video><div v-else-if="assetDisplayMediaKind(asset) === 'audio'" class="asset-card-audio-cover"><Film :size="34" /><small>音频素材</small></div><img v-else :src="asset.image" :alt="asset.title" :class="{ contain: asset.title.includes('标志') }" @error="handleAssetImageError" /><span>{{ asset.version }}</span><i class="asset-open-hint"><Eye :size="15" />查看详情</i></div><div class="asset-copy"><div><h3>{{ asset.title }}</h3><p>{{ asset.category }} · {{ asset.updated }} · {{ asset.uploaderName || '未知上传者' }}</p></div><button aria-label="查看详情" @click.stop="openAsset(asset)"><MoreHorizontal :size="16" /></button></div><div class="asset-foot"><span :class="statusClass(asset.status)">{{ asset.status }}</span><button v-if="canManageAsset(asset)" @click.stop="cycleAsset(asset)">更改状态</button><small v-else>仅可查看</small></div></article></div>
        <div v-else class="empty-state"><Upload :size="28" /><h3>这一分类还没有素材</h3><button v-if="canEditContent" class="button primary" :disabled="uploading" @click="openUploadDialog"><RefreshCw v-if="uploading" class="spin-inline" :size="16" />{{ uploading ? uploadLabel : '上传第一份素材' }}</button><p v-else>当前账号仅可查看素材</p></div>
        <div class="policy-grid"><article><Eye :size="18" /><h3>当前版本</h3><p>团队默认使用的素材文件</p></article><article><History :size="18" /><h3>前一版本</h3><p>替换时自动保留，可一键恢复</p></article><article><Archive :size="18" /><h3>历史清理</h3><p>第三版上传后自动清理最早版本</p></article></div>
      </section>

      <section v-if="view === 'members' && isAdmin" class="page page--medium admin-page">
        <div class="page-title"><div><p class="eyebrow">ADMINISTRATION</p><h1>后台管理</h1><p>集中管理团队账号、访问权限和剧本发布审核</p></div></div>
        <div class="admin-tabs" role="tablist" aria-label="后台管理分类">
          <button role="tab" :aria-selected="adminTab === 'members'" :class="{ active: adminTab === 'members' }" @click="adminTab = 'members'"><Users :size="16" /><span>人员管理</span><small>{{ members.length }}</small></button>
          <button role="tab" :aria-selected="adminTab === 'reviews'" :class="{ active: adminTab === 'reviews' }" @click="adminTab = 'reviews'; reviewEpisodeId = pendingReviews[0]?.id ?? episodes[0]?.id ?? 1"><ShieldCheck :size="16" /><span>剧本审核</span><small :class="{ alert: pendingReviews.length }">{{ pendingReviews.length }}</small></button>
        </div>

        <template v-if="adminTab === 'members'">
          <section class="panel invite-panel"><div class="invite-title"><span class="soft-icon"><UserCog :size="18" /></span><div><h3>邀请新成员</h3><p>网站不开放自由注册，只有管理员可以邀请</p></div></div><div class="invite-form"><input v-model="inviteEmail" type="email" placeholder="成员邮箱" @keyup.enter="invite" /><label><select v-model="inviteRole"><option>编辑人员</option><option>审阅人员</option><option>查看人员</option><option>管理员</option></select><ChevronDown :size="14" /></label><button class="button primary" @click="invite">发送邀请</button></div></section>
          <section class="panel member-panel"><div class="member-head"><div><h3>项目成员</h3><p>共 {{ members.length }} 人</p></div><ShieldCheck :size="17" /></div><div class="table-wrap"><table><thead><tr><th>成员</th><th>角色</th><th>状态</th><th>核心权限</th><th>操作</th></tr></thead><tbody><tr v-for="member in members" :key="member.id"><td><div class="member-cell"><span class="avatar">{{ member.initials }}</span><span><b>{{ member.name }}</b><small>{{ member.email }}</small></span></div></td><td><select :value="member.role" :disabled="member.id === 1" @change="updateMember(member, { role: ($event.target as HTMLSelectElement).value as MemberRole })"><option>管理员</option><option>编辑人员</option><option>审阅人员</option><option>查看人员</option></select></td><td><span :class="statusClass(member.status)">{{ member.status }}</span></td><td>{{ rolePermission(member.role) }}</td><td><button :disabled="member.id === 1" @click="updateMember(member, { status: member.status === '已停用' ? '已启用' : '已停用' })">{{ member.status === '已停用' ? '启用' : '停用' }}</button></td></tr></tbody></table></div></section>
          <div class="roles-grid"><article v-for="role in ([['管理员','发布版本、管理账号'],['编辑人员','改草稿、上传素材'],['审阅人员','评论、提交审核意见'],['查看人员','只读及授权下载']] as const)" :key="role[0]"><h3>{{ role[0] }}</h3><p>{{ role[1] }}</p></article></div>
        </template>

        <template v-else>
          <div class="review-stats"><article><span class="soft-icon"><Clock3 :size="18" /></span><div><small>待审核</small><strong>{{ pendingReviews.length }}</strong></div></article><article><span class="soft-icon"><BookOpenText :size="18" /></span><div><small>本季剧集</small><strong>{{ episodes.length }}</strong></div></article><article><span class="soft-icon"><Check :size="18" /></span><div><small>已有正式版本</small><strong>{{ episodes.filter(item => item.version > 0).length }}</strong></div></article></div>
          <section class="review-workspace panel">
            <aside class="review-list"><header><div><h3>审核队列</h3><p>{{ pendingReviews.length ? `${pendingReviews.length} 个剧本等待处理` : '当前没有待审核剧本' }}</p></div><ShieldCheck :size="17" /></header><div><button v-for="episode in episodes" :key="episode.id" :class="{ active: reviewEpisode?.id === episode.id }" @click="reviewEpisodeId = episode.id"><span><small>{{ episode.code }}</small><b>{{ episode.title }}</b></span><em :class="statusClass(episode.reviewReady ? '待审核' : episode.status)">{{ episode.reviewReady ? '待审核' : episode.status }}</em></button></div></aside>
            <article v-if="reviewEpisode" class="review-detail"><header><div><p class="eyebrow">SCRIPT REVIEW</p><h2>{{ reviewEpisode.code }}《{{ reviewEpisode.title }}》</h2><span>当前正式版 V{{ reviewEpisode.version }} · {{ reviewEpisode.reviewReady ? '等待管理员审核' : '暂无待发布审核' }}</span></div><span :class="statusClass(reviewEpisode.reviewReady ? '待审核' : reviewEpisode.status)">{{ reviewEpisode.reviewReady ? '待审核' : reviewEpisode.status }}</span></header><div class="review-compare"><section><div><span>CURRENT VERSION</span><b>正式版 V{{ reviewEpisode.version }}</b></div><pre>{{ reviewEpisode.current }}</pre></section><section :class="{ pending: reviewEpisode.reviewReady }"><div><span>SUBMITTED DRAFT</span><b>待发布草稿</b></div><pre>{{ reviewEpisode.draft }}</pre></section></div><footer><button class="button outline" @click="openEpisodeInScriptRoom(reviewEpisode)"><Eye :size="15" />进入剧本中心</button><button v-if="reviewEpisode.reviewReady" class="button primary" :disabled="busy" @click="publishReview(reviewEpisode)"><ShieldCheck :size="15" />审核并发布为 V{{ reviewEpisode.version + 1 }}</button><span v-else><Check :size="14" />此剧本当前无需审核</span></footer></article>
          </section>
        </template>
      </section>
    </section>

    <input id="asset-file-input" ref="fileInput" class="file-input-hidden" multiple type="file" :accept="uploadDraft.category === '视频音频' ? 'video/*,audio/*' : isGalleryCategory(uploadDraft.category) ? 'image/*' : 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip'" @change="uploadFiles" />
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="selectedPrompt || promptDialogMode === 'create'" class="asset-modal prompt-modal" role="dialog" aria-modal="true" aria-labelledby="prompt-dialog-title" @click.self="closePrompt">
          <section class="prompt-dialog" :class="{ 'prompt-dialog--editing': promptDialogMode !== 'view' }">
            <button class="prompt-dialog-close" type="button" aria-label="关闭提示词详情" @click="closePrompt"><X :size="20" /></button>
            <template v-if="promptDialogMode === 'view' && selectedPrompt">
              <header><div><p class="eyebrow">PROMPT DETAIL · {{ selectedPrompt.category }}</p><h2 id="prompt-dialog-title">{{ selectedPrompt.title }}</h2><span>{{ selectedPrompt.description }}</span></div><em>{{ selectedPrompt.model }}</em></header>
              <div class="prompt-dialog-tags"><span v-for="tag in selectedPrompt.tags" :key="tag"># {{ tag }}</span></div>
              <div class="prompt-full-content"><div><span>完整提示词</span><small>可直接复制，使用时替换方括号中的变量</small></div><pre>{{ selectedPrompt.content }}</pre></div>
              <footer><span><Clock3 :size="14" />{{ selectedPrompt.updated }} 更新 · 已使用 {{ selectedPrompt.uses }} 次</span><div><button class="button outline" @click="closePrompt">关闭</button><button v-if="canEditContent" class="button outline prompt-edit-button" @click="startPromptEdit"><PencilLine :size="15" />编辑提示词</button><button class="button primary" @click="copyPrompt(selectedPrompt)"><Copy :size="15" />复制提示词</button></div></footer>
            </template>
            <form v-else class="prompt-editor-form" @submit.prevent="savePrompt">
              <header><div><p class="eyebrow">{{ promptDialogMode === 'create' ? 'ADD TO PROMPT LIBRARY' : 'EDIT PROMPT' }}</p><h2 id="prompt-dialog-title">{{ promptDialogMode === 'create' ? '上传提示词' : '编辑提示词' }}</h2><span>{{ promptDialogMode === 'create' ? '填写模板资料，也可以从 TXT 或 Markdown 文件导入正文' : '修改后保存，提示词卡片和团队数据会立即同步' }}</span></div></header>
              <div class="prompt-editor-fields">
                <label><span>提示词名称 *</span><input v-model="promptDraft.title" maxlength="80" placeholder="例如：夜景人物近景定妆照" /></label>
                <label><span>分类 *</span><input v-model="promptDraft.category" maxlength="30" list="prompt-category-options" placeholder="例如：人物角色" /><datalist id="prompt-category-options"><option value="人物角色" /><option value="场景空间" /><option value="分镜画面" /><option value="视频运镜" /><option value="视觉风格" /></datalist></label>
                <label class="prompt-editor-wide"><span>适用模型 *</span><input v-model="promptDraft.model" maxlength="60" placeholder="例如：Midjourney / 即梦 / 可灵" /></label>
                <label class="prompt-editor-wide"><span>用途说明 *</span><textarea v-model="promptDraft.description" maxlength="300" rows="3" placeholder="说明这条提示词适合解决什么问题"></textarea></label>
                <label class="prompt-editor-wide prompt-content-field"><span>提示词正文 *</span><textarea v-model="promptDraft.content" maxlength="20000" rows="10" placeholder="输入完整提示词，可用 [变量名称] 标记使用时需要替换的内容"></textarea></label>
                <label class="prompt-editor-wide"><span>标签 <small>使用逗号分隔，最多 12 个</small></span><input v-model="promptDraft.tags" maxlength="300" placeholder="例如：定妆照，写实人物，角色一致性" /></label>
                <div class="prompt-file-import"><input ref="promptFileInput" class="file-input-hidden" type="file" accept=".txt,.md,.markdown,text/plain,text/markdown" @change="importPromptFile" /><button class="button outline" type="button" @click="promptFileInput?.click()"><FileText :size="15" />从 TXT / Markdown 导入正文</button><small>仅导入正文内容，不会覆盖已填写的分类、模型和说明</small></div>
              </div>
              <footer><span><ShieldCheck :size="14" />保存后团队成员可立即搜索和使用</span><div><button class="button outline" type="button" :disabled="busy" @click="cancelPromptEdit">取消</button><button class="button primary" type="submit" :disabled="busy"><RefreshCw v-if="busy" class="spin-inline" :size="15" /><Check v-else :size="15" />{{ busy ? '正在保存' : promptDialogMode === 'create' ? '确认上传' : '保存修改' }}</button></div></footer>
            </form>
            <Transition name="save-confirm"><div v-if="promptSaveNotice" class="prompt-save-notice" role="status"><Check :size="17" /><span><b>保存成功</b><small>{{ promptSaveNotice }}</small></span></div></Transition>
          </section>
        </div>
      </Transition>
      <Transition name="modal">
        <div v-if="uploadDialogOpen" class="asset-modal upload-modal" role="dialog" aria-modal="true" aria-labelledby="upload-dialog-title" @click.self="closeUploadDialog">
          <section class="upload-dialog" :class="{ 'upload-dialog--character': uploadDraft.category === '人物' }">
            <header class="upload-dialog-head">
              <div><p class="eyebrow">ADD TO ASSET LIBRARY</p><h2 id="upload-dialog-title">上传素材</h2><span>选择分类并补充资料，也可以保留默认设置直接上传</span></div>
              <button type="button" aria-label="关闭上传弹窗" :disabled="uploading" @click="closeUploadDialog"><X :size="20" /></button>
            </header>
            <form class="upload-form" @submit.prevent="submitUpload()">
              <fieldset class="upload-category-field">
                <legend>素材分类 <small>已根据当前页面自动选择，可修改</small></legend>
                <div class="upload-category-grid">
                  <label v-for="category in ['人物','场景','分镜','服装道具','视频音频']" :key="category" :class="{ active: uploadDraft.category === category }">
                    <input v-model="uploadDraft.category" type="radio" name="upload-category" :value="category" />
                    <span>{{ category }}</span><Check :size="13" />
                  </label>
                </div>
              </fieldset>

              <div class="upload-fields" :class="{ 'upload-fields--single': uploadDraft.category === '人物' }">
                <label v-if="uploadDraft.category === '人物'"><span>角色名称 <small>选填</small></span><input v-model="uploadCharacterDraft.name" maxlength="40" :placeholder="uploadFilesDraft.length > 1 ? '多张图片将归入同一个角色' : '留空时自动使用文件名'" /></label>
                <label v-else><span>素材名称 <small>选填</small></span><input v-model="uploadDraft.title" :disabled="uploadFilesDraft.length > 1 && !isGalleryCategory(uploadDraft.category)" maxlength="80" :placeholder="uploadFilesDraft.length > 1 ? (isGalleryCategory(uploadDraft.category) ? `多个${uploadDraft.category === '视频音频' ? '文件' : '图片'}将归入同一项素材` : '批量上传将自动使用各文件名') : '留空时自动使用文件名'" /></label>
                <label v-if="uploadDraft.category !== '人物'"><span>{{ uploadContentCopy.label }} <small>选填</small></span><textarea v-model="uploadDraft.description" maxlength="500" rows="3" :placeholder="uploadContentCopy.placeholder"></textarea></label>
              </div>

              <section v-if="uploadDraft.category === '人物'" class="upload-character-panel">
                <header><div><span>人物特征档案</span><small>CHARACTER PROFILE</small></div><p>全部选填，标签类内容可每行填写一项</p></header>
                <div class="upload-character-body">
                  <div class="upload-character-section">
                    <h3>角色身份</h3>
                    <div class="upload-character-grid upload-character-grid--three">
                      <label><span>中文名</span><input v-model="uploadCharacterDraft.name" maxlength="40" placeholder="角色姓名" /></label>
                      <label><span>英文名</span><input v-model="uploadCharacterDraft.englishName" maxlength="60" placeholder="English name" /></label>
                      <label><span>角色定位</span><input v-model="uploadCharacterDraft.role" maxlength="80" placeholder="例如：资深猎头 / 客户方负责人" /></label>
                    </div>
                  </div>
                  <div class="upload-character-section">
                    <h3>基础资料</h3>
                    <div class="upload-character-grid upload-character-grid--four">
                      <label><span>年龄</span><input v-model.number="uploadCharacterDraft.age" type="number" min="0" max="150" placeholder="年龄" /></label>
                      <label><span>生日</span><input v-model="uploadCharacterDraft.birthday" maxlength="30" placeholder="例如：3月18日" /></label>
                      <label><span>星座</span><input v-model="uploadCharacterDraft.zodiac" maxlength="20" placeholder="例如：双鱼座" /></label>
                      <label><span>身高</span><input v-model="uploadCharacterDraft.height" maxlength="20" placeholder="例如：178cm" /></label>
                      <label><span>籍贯</span><input v-model="uploadCharacterDraft.hometown" maxlength="60" placeholder="人物籍贯" /></label>
                      <label><span>教育背景</span><input v-model="uploadCharacterDraft.education" maxlength="100" placeholder="学校 / 专业" /></label>
                      <label><span>职业</span><input v-model="uploadCharacterDraft.occupation" maxlength="100" placeholder="当前职业" /></label>
                    </div>
                  </div>
                  <div class="upload-character-section">
                    <h3>人物表达</h3>
                    <div class="upload-character-grid upload-character-grid--two">
                      <label><span>人物金句</span><textarea v-model="uploadCharacterDraft.quote" maxlength="300" rows="3" placeholder="最能代表这个角色的一句话"></textarea></label>
                      <label><span>人物简介</span><textarea v-model="uploadCharacterDraft.bio" maxlength="1000" rows="3" placeholder="人物经历、关系、目标与当前处境"></textarea></label>
                    </div>
                  </div>
                  <div class="upload-character-section">
                    <h3>特征标签</h3>
                    <div class="upload-character-grid upload-character-grid--four upload-character-tags">
                      <label><span>性格底色</span><textarea v-model="uploadCharacterDraft.personality" maxlength="500" rows="4" placeholder="果断&#10;克制&#10;洞察力强"></textarea></label>
                      <label><span>职业天赋</span><textarea v-model="uploadCharacterDraft.talents" maxlength="500" rows="4" placeholder="候选人判断&#10;谈判推进"></textarea></label>
                      <label><span>人物弱点</span><textarea v-model="uploadCharacterDraft.weaknesses" maxlength="500" rows="4" placeholder="过度理性&#10;不轻易信任别人"></textarea></label>
                      <label><span>行为习惯</span><textarea v-model="uploadCharacterDraft.habits" maxlength="500" rows="4" placeholder="思考时转动钢笔&#10;习惯提前到场"></textarea></label>
                    </div>
                  </div>
                  <div class="upload-character-section">
                    <h3>形象设定</h3>
                    <div class="upload-character-grid upload-character-grid--one">
                      <label><span>外貌、体态与服装特征</span><textarea v-model="uploadCharacterDraft.appearance" maxlength="800" rows="4" placeholder="43岁，身材挺拔，有真实中年感&#10;两鬓少量灰发，眼神克制但疲惫&#10;深色西装、白衬衫&#10;独处时会松开领口，在车里发呆"></textarea></label>
                    </div>
                  </div>
                  <div class="upload-character-section">
                    <h3>标志物</h3>
                    <div class="upload-character-grid upload-character-grid--one">
                      <label><span>能够强化角色识别的随身物品</span><textarea v-model="uploadCharacterDraft.signatureItems" maxlength="600" rows="3" placeholder="机械腕表：谈到组织调整时会下意识触碰&#10;婚戒&#10;商务手机&#10;深色公文包"></textarea></label>
                    </div>
                  </div>
                  <div class="upload-character-section">
                    <h3>人物关系</h3>
                    <div class="upload-character-grid upload-character-grid--one">
                      <label><span>每行填写一个“人物：关系描述”</span><textarea v-model="uploadCharacterDraft.relationships" maxlength="1200" rows="5" placeholder="Mia：从质疑她的年龄，到第一次愿意认真听她说完&#10;彭渝淞：彼此评估，也共同面对公司内部权力边界&#10;妻子与女儿：职业选择中最真实的决策者"></textarea></label>
                    </div>
                  </div>
                </div>
              </section>

              <button class="upload-dropzone" :class="{ dragging: uploadDragging, filled: uploadFilesDraft.length }" type="button" @click="openFilePicker" @dragenter.prevent="uploadDragging = true" @dragover.prevent="uploadDragging = true" @dragleave.prevent="uploadDragging = false" @drop.prevent="handleUploadDrop">
                <span class="upload-drop-icon"><Upload :size="22" /></span>
                <span><b>{{ uploadFilesDraft.length ? `已选择 ${uploadFilesDraft.length} 个文件` : '选择文件或拖到这里（选填）' }}</b><small>{{ uploadDraft.category === '视频音频' ? '可上传多个视频或音频，所有文件会归入同一项素材；单个不超过 80MB' : isGalleryCategory(uploadDraft.category) ? `可上传多张图片，所有图片会归入同一项${uploadDraft.category}素材；单张不超过 80MB` : '支持图片、视频、音频和常用文档，单个文件不超过 80MB' }}</small></span>
                <em>{{ uploadFilesDraft.length ? (isGalleryCategory(uploadDraft.category) ? `继续添加${uploadDraft.category === '视频音频' ? '文件' : '图片'}` : '重新选择') : '浏览文件' }}</em>
              </button>

              <div v-if="uploadFilesDraft.length" class="upload-file-list">
                <div v-for="(file, index) in uploadFilesDraft" :key="`${file.name}-${file.lastModified}-${index}`">
                  <span><FileText :size="16" /></span>
                  <p><b>{{ file.name }}</b><small>{{ formatFileSize(file.size) }}</small></p>
                  <button type="button" :aria-label="`移除 ${file.name}`" :disabled="uploading" @click="removeUploadFile(index)"><X :size="15" /></button>
                </div>
                <button v-if="isGalleryCategory(uploadDraft.category)" class="upload-file-add" type="button" :disabled="uploading" @click="openFilePicker"><Plus :size="14" />继续添加{{ uploadDraft.category === '视频音频' ? '文件' : '图片' }}</button>
              </div>

              <footer class="upload-dialog-actions">
                <p><ShieldCheck :size="14" /><span>{{ uploadDraft.category === '人物' ? '所有人物资料均可留空，上传后仍可编辑' : '名称和说明均可留空，上传后仍可编辑' }}</span></p>
                <div><button class="button outline" type="button" :disabled="uploading" @click="closeUploadDialog">取消</button><button class="button primary" type="submit" :disabled="uploading"><RefreshCw v-if="uploading" class="spin-inline" :size="15" /><Upload v-else :size="15" />{{ uploading ? uploadLabel : '开始上传' }}</button></div>
              </footer>
            </form>
            <div v-if="uploadNoFileConfirmOpen" class="upload-no-file-confirm" role="alertdialog" aria-modal="true" aria-labelledby="no-file-confirm-title">
              <section>
                <span class="upload-confirm-icon"><Image :size="24" /></span>
                <h3 id="no-file-confirm-title">当前没有素材图片</h3>
                <p>没有选择图片或文件，是否仍要继续上传？已填写的人物资料会正常保存，素材将暂时使用默认占位图。</p>
                <div><button class="button outline" type="button" @click="uploadNoFileConfirmOpen = false">返回添加图片</button><button class="button primary" type="button" @click="submitUpload(true)">继续上传</button></div>
              </section>
            </div>
          </section>
        </div>
      </Transition>
      <Transition name="modal">
        <div v-if="selectedAsset" class="asset-modal" role="dialog" aria-modal="true" :aria-label="`${selectedAsset.title} 素材详情`" @click.self="closeAsset">
          <section class="asset-dialog" :class="{ 'character-dialog': activeCharacterProfile }">
            <button class="dialog-close" aria-label="关闭素材详情" @click="closeAsset"><X :size="20" /></button>
            <div class="dialog-media" :class="{ 'character-visual': activeCharacterProfile, 'asset-gallery-visual': !activeCharacterProfile && isGalleryCategory(selectedAsset.category), 'asset-preview-stage': !activeCharacterProfile && !isGalleryCategory(selectedAsset.category) }">
              <template v-if="activeCharacterProfile">
                <div class="visual-head"><span>角色视觉设定</span><small>{{ activeCharacterProfile.name }} · {{ selectedAsset.version }}</small></div>
                <div class="visual-board">
                  <div class="visual-portrait"><img :src="characterImageSrc" :alt="`${activeCharacterProfile.name} 正面肖像`" @error="handleAssetImageError" /></div>
                  <div class="visual-poses">
                    <button v-for="(image, index) in characterImages" :key="image" type="button" :class="{ active: characterImageSrc === image }" :aria-label="`查看${activeCharacterProfile.name}的第 ${index + 1} 张图片`" @click="selectedCharacterImage = image">
                      <img :src="image" :alt="`${activeCharacterProfile.name} 角色图片 ${index + 1}`" @error="handleAssetImageError" /><span>图 {{ index + 1 }}</span>
                    </button>
                  </div>
                </div>
                <div class="visual-foot character-visual-toolbar">
                  <span><i></i>第 {{ selectedCharacterImageIndex + 1 }} / {{ characterImages.length }} 张</span>
                  <div>
                    <button v-if="canManageSelectedAsset" class="display-image-button" type="button" :class="{ active: characterImageSrc === selectedAsset.image }" :disabled="busy || characterImageSrc === selectedAsset.image" @click="setSelectedAsDisplayImage"><Check v-if="characterImageSrc === selectedAsset.image" :size="13" /><Image v-else :size="13" />{{ characterImageSrc === selectedAsset.image ? '当前展示图' : '设为展示图' }}</button>
                    <button type="button" @click="openImagePreview"><Maximize2 :size="13" />放大查看</button>
                    <button type="button" @click="downloadSelectedImage"><Download :size="13" />下载保存</button>
                  </div>
                </div>
              </template>
              <template v-else-if="isGalleryCategory(selectedAsset.category)">
                <div class="visual-head"><span>{{ selectedAsset.category }}{{ selectedAsset.category === '视频音频' ? '文件集' : '图片集' }}</span><small>{{ selectedAsset.title }} · {{ selectedAsset.version }}</small></div>
                <div class="asset-gallery-main" :class="`media-${selectedGalleryMediaKind}`">
                  <video v-if="selectedGalleryMediaKind === 'video'" :src="characterImageSrc" controls preload="metadata"></video>
                  <div v-else-if="selectedGalleryMediaKind === 'audio'" class="asset-audio-player"><Film :size="38" /><strong>{{ selectedAsset.title }}</strong><small>音频 {{ selectedCharacterImageIndex + 1 }}</small><audio :src="characterImageSrc" controls preload="metadata"></audio></div>
                  <button v-else type="button" :aria-label="`放大查看：${selectedAsset.title}`" @click="openImagePreview"><img :src="characterImageSrc" :alt="`${selectedAsset.title} 图片 ${selectedCharacterImageIndex + 1}`" @error="handleAssetImageError" /><span><Maximize2 :size="16" />点击放大查看</span></button>
                </div>
                <div class="visual-poses asset-gallery-thumbnails">
                  <button v-for="(image, index) in characterImages" :key="image" type="button" :class="{ active: characterImageSrc === image }" :aria-label="`查看第 ${index + 1} 个文件`" @click="selectedCharacterImage = image">
                    <video v-if="galleryMediaKind(image) === 'video'" :src="image" muted preload="metadata"></video><div v-else-if="galleryMediaKind(image) === 'audio'" class="asset-audio-thumbnail"><Film :size="24" /><small>音频</small></div><img v-else :src="image" :alt="`${selectedAsset.title} 图片 ${index + 1}`" @error="handleAssetImageError" /><span>{{ selectedAsset.category === '视频音频' ? '文件' : '图' }} {{ index + 1 }}</span>
                  </button>
                </div>
                <div class="visual-foot character-visual-toolbar">
                  <span><i></i>第 {{ selectedCharacterImageIndex + 1 }} / {{ characterImages.length }} {{ selectedAsset.category === '视频音频' ? '个' : '张' }}</span>
                  <div><button v-if="canManageSelectedAsset" class="display-image-button" type="button" :class="{ active: characterImageSrc === selectedAsset.image }" :disabled="busy || characterImageSrc === selectedAsset.image" @click="setSelectedAsDisplayImage"><Check v-if="characterImageSrc === selectedAsset.image" :size="13" /><Image v-else :size="13" />{{ characterImageSrc === selectedAsset.image ? '当前展示图' : '设为展示图' }}</button><button v-if="selectedGalleryMediaKind !== 'audio'" type="button" @click="openImagePreview"><Maximize2 :size="13" />{{ selectedGalleryMediaKind === 'video' ? '全屏查看' : '放大查看' }}</button><button type="button" @click="downloadSelectedImage"><Download :size="13" />下载保存</button></div>
                </div>
              </template>
              <template v-else>
                <div class="asset-preview-toolbar"><span>{{ selectedAsset.category }}</span><small>{{ selectedAsset.version }}</small></div>
                <button class="asset-preview-click" type="button" :aria-label="`查看大图：${selectedAsset.title}`" @click="openImagePreview">
                  <img :src="selectedAsset.image" :alt="selectedAsset.title" :class="{ contain: selectedAsset.title.includes('标志') }" @error="handleAssetImageError" />
                  <span><Eye :size="18" />点击查看大图</span>
                </button>
                <div class="asset-preview-caption"><span>素材预览</span><small>保持原始比例展示</small></div>
              </template>
            </div>
            <div v-if="activeCharacterProfile" class="dialog-content character-content">
              <div class="character-summary">
                <p class="eyebrow">CHARACTER PROFILE · 角色档案</p>
                <div class="character-heading"><div><h2>{{ activeCharacterProfile.name }}</h2><p>{{ activeCharacterProfile.englishName }}</p><span>{{ activeCharacterProfile.role }}</span></div><span :class="statusClass(selectedAsset.status)">{{ selectedAsset.status }} · {{ selectedAsset.version }}</span></div>
                <div class="character-facts">
                  <div><small>年龄</small><strong>{{ activeCharacterProfile.age || '—' }}<em v-if="activeCharacterProfile.age">岁</em></strong></div>
                  <div><small>星座</small><strong>{{ activeCharacterProfile.zodiac }}</strong></div>
                  <div><small>身高</small><strong>{{ activeCharacterProfile.height }}</strong></div>
                  <div><small>生日</small><strong>{{ activeCharacterProfile.birthday }}</strong></div>
                </div>
              </div>
              <div v-if="profileEditing && characterDraft" class="character-details character-edit-details">
                <form class="character-edit-form" @submit.prevent="saveProfile">
                  <div class="edit-section-title"><div><p class="eyebrow">EDITING MODE</p><h3>编辑角色资料</h3></div><span>每行一个标签，保存后立即同步</span></div>
                  <div class="character-image-editor" :class="{ selected: characterGalleryChanged }">
                    <input ref="characterImageInput" class="file-input-hidden" type="file" accept="image/*" @change="selectCharacterImage" />
                    <input ref="characterImageAddInput" class="file-input-hidden" multiple type="file" accept="image/*" @change="addCharacterImages" />
                    <span><Image :size="20" /></span>
                    <div class="character-image-copy"><b>角色图片 · {{ characterImages.length }} 张</b><small>当前选中第 {{ selectedCharacterImageIndex + 1 }} 张；更换和删除前会再次显示目标图片供确认</small></div>
                    <div class="character-image-actions">
                      <button class="button outline small" type="button" :disabled="busy" @click="openCharacterImagePicker('add')"><Plus :size="13" />添加图片</button>
                      <button class="button outline small" type="button" :disabled="busy || !characterImages.length" @click="requestCharacterImageAction('replace')"><RefreshCw :size="13" />更换图片</button>
                      <button class="button outline small character-image-delete" type="button" :disabled="busy || characterImages.length <= 1" @click="requestCharacterImageAction('delete')"><Trash2 :size="13" />删除图片</button>
                    </div>
                  </div>
                  <div class="edit-grid edit-grid--three">
                    <label><span>中文名 *</span><input v-model="characterDraft.name" /></label><label><span>英文名</span><input v-model="characterDraft.englishName" /></label><label><span>角色定位</span><input v-model="characterDraft.role" /></label>
                  </div>
                  <div class="edit-grid edit-grid--four">
                    <label><span>年龄</span><input v-model.number="characterDraft.age" type="number" min="0" /></label><label><span>生日</span><input v-model="characterDraft.birthday" /></label><label><span>星座</span><input v-model="characterDraft.zodiac" /></label><label><span>身高</span><input v-model="characterDraft.height" /></label>
                  </div>
                  <div class="edit-grid edit-grid--two">
                    <label><span>籍贯</span><input v-model="characterDraft.hometown" /></label><label><span>教育背景</span><input v-model="characterDraft.education" /></label><label><span>职业</span><input v-model="characterDraft.occupation" /></label><label><span>人物金句</span><input v-model="characterDraft.quote" /></label>
                  </div>
                  <label class="edit-wide"><span>人物简介 *</span><textarea v-model="characterDraft.bio" rows="4"></textarea></label>
                  <div class="edit-grid edit-grid--two edit-tag-fields">
                    <label><span>性格底色</span><textarea v-model="characterDraft.personality" rows="5"></textarea></label><label><span>职业天赋</span><textarea v-model="characterDraft.talents" rows="5"></textarea></label><label><span>人物弱点</span><textarea v-model="characterDraft.weaknesses" rows="5"></textarea></label><label><span>行为习惯</span><textarea v-model="characterDraft.habits" rows="5"></textarea></label>
                    <label><span>形象设定</span><textarea v-model="characterDraft.appearance" rows="5" placeholder="每行一项"></textarea></label><label><span>标志物</span><textarea v-model="characterDraft.signatureItems" rows="5" placeholder="每行一项"></textarea></label><label class="edit-span-two"><span>人物关系</span><textarea v-model="characterDraft.relationships" rows="6" placeholder="每行填写一个“人物：关系描述”"></textarea></label>
                  </div>
                </form>
              </div>
              <div v-else class="character-details">
                <blockquote>{{ activeCharacterProfile.quote }}</blockquote>
                <section class="profile-section"><h3>人物简介</h3><p>{{ activeCharacterProfile.bio }}</p></section>
                <section class="profile-section"><h3>基础信息</h3><dl class="profile-basics"><div><dt>籍贯</dt><dd>{{ activeCharacterProfile.hometown }}</dd></div><div><dt>教育背景</dt><dd>{{ activeCharacterProfile.education }}</dd></div><div><dt>职业</dt><dd>{{ activeCharacterProfile.occupation }}</dd></div><div><dt>角色定位</dt><dd>{{ activeCharacterProfile.role }}</dd></div></dl></section>
                <section class="profile-section"><h3>性格底色</h3><div class="trait-grid"><span v-for="item in activeCharacterProfile.personality" :key="item">{{ item }}</span></div></section>
                <section class="profile-section"><h3>职业天赋</h3><div class="trait-grid"><span v-for="item in activeCharacterProfile.talents" :key="item">{{ item }}</span></div></section>
                <section class="profile-section"><h3>人物弱点</h3><div class="trait-grid danger"><span v-for="item in activeCharacterProfile.weaknesses" :key="item">{{ item }}</span></div></section>
                <section class="profile-section"><h3>行为习惯</h3><div class="trait-grid"><span v-for="item in activeCharacterProfile.habits" :key="item">{{ item }}</span></div></section>
                <section class="profile-section"><h3>形象设定</h3><div class="trait-grid appearance-grid"><span v-for="item in activeCharacterProfile.appearance ?? ['形象设定待补充']" :key="item">{{ item }}</span></div></section>
                <section class="profile-section"><h3>标志物</h3><div class="trait-grid signature-grid"><span v-for="item in activeCharacterProfile.signatureItems ?? ['标志物待补充']" :key="item">{{ item }}</span></div></section>
                <section class="profile-section"><h3>人物关系</h3><div class="relationship-list"><p v-for="item in activeCharacterProfile.relationships ?? ['人物关系待补充']" :key="item">{{ item }}</p></div></section>
              </div>
              <div class="dialog-actions character-footer">
                <template v-if="profileEditing"><button class="button outline" :disabled="busy" @click="cancelProfileEdit">取消编辑</button><button class="button primary profile-save-button" :disabled="busy" @click="saveProfile"><RefreshCw v-if="busy" class="spin-inline" :size="15" /><Check v-else :size="15" />{{ busy ? '正在保存…' : '保存资料' }}</button></template>
                <template v-else><span v-if="!canManageSelectedAsset" class="permission-note"><LockKeyhole :size="13" />仅上传者或管理员可编辑</span><button v-if="canManageSelectedAsset" class="button edit-profile-button" @click="startProfileEdit"><PencilLine :size="15" />编辑角色资料</button><a class="button outline" :href="characterImageSrc" target="_blank" rel="noopener"><Eye :size="15" />查看角色原图</a><button v-if="canManageSelectedAsset" class="button primary" @click="cycleAsset(selectedAsset)">更改确认状态</button><button v-if="canManageSelectedAsset" class="button danger-button" @click="requestAssetDelete"><Trash2 :size="15" />删除</button></template>
              </div>
            </div>
            <div v-else class="dialog-content standard-asset-content">
              <div class="asset-detail-kicker"><p class="eyebrow">ASSET DETAIL · 素材详情</p><span :class="statusClass(selectedAsset.status)">{{ selectedAsset.status }}</span></div>
              <div class="dialog-title"><div><h2>{{ selectedAsset.title }}</h2><p>数字资产库&nbsp;&nbsp;/&nbsp;&nbsp;{{ selectedAsset.category }}</p></div></div>
              <p v-if="selectedAsset.description && !assetEditing" class="asset-description">{{ selectedAsset.description }}</p>
              <div v-if="assetEditing" class="asset-edit-panel">
                <div v-if="isGalleryCategory(selectedAsset.category)" class="character-image-editor asset-gallery-editor" :class="{ selected: characterGalleryChanged }">
                  <input ref="characterImageInput" class="file-input-hidden" type="file" :accept="selectedAsset.category === '视频音频' ? 'video/*,audio/*' : 'image/*'" @change="selectCharacterImage" />
                  <input ref="characterImageAddInput" class="file-input-hidden" multiple type="file" :accept="selectedAsset.category === '视频音频' ? 'video/*,audio/*' : 'image/*'" @change="addCharacterImages" />
                  <span><Image :size="20" /></span>
                  <div class="character-image-copy"><b>{{ selectedAsset.category }}{{ selectedAsset.category === '视频音频' ? '文件' : '图片' }} · {{ characterImages.length }} {{ selectedAsset.category === '视频音频' ? '个' : '张' }}</b><small>当前选中第 {{ selectedCharacterImageIndex + 1 }} 个；更换和删除前会显示目标文件供确认</small></div>
                  <div class="character-image-actions"><button class="button outline small" type="button" :disabled="busy" @click="openCharacterImagePicker('add')"><Plus :size="13" />添加{{ selectedAsset.category === '视频音频' ? '文件' : '图片' }}</button><button class="button outline small" type="button" :disabled="busy || !characterImages.length" @click="requestCharacterImageAction('replace')"><RefreshCw :size="13" />更换</button><button class="button outline small character-image-delete" type="button" :disabled="busy || characterImages.length <= 1" @click="requestCharacterImageAction('delete')"><Trash2 :size="13" />删除</button></div>
                </div>
                <label><span>素材名称</span><input v-model="assetDraft.title" /></label>
                <label><span>素材分类</span><select v-model="assetDraft.category"><option>人物</option><option>场景</option><option>分镜</option><option>服装道具</option><option>视频音频</option></select></label>
                <label><span>素材状态</span><select v-model="assetDraft.status"><option>修改中</option><option>待确认</option><option>已锁定</option></select></label>
                <label><span>上传者</span><input :value="selectedAsset.uploaderName || '未知成员'" disabled /></label>
                <label class="asset-edit-description"><span>素材说明</span><textarea v-model="assetDraft.description" maxlength="500" rows="3" placeholder="可留空"></textarea></label>
              </div>
              <template v-else>
                <dl class="asset-meta"><div><dt>当前版本</dt><dd>{{ selectedAsset.version }}</dd></div><div><dt>素材类型</dt><dd>{{ selectedAsset.category }}</dd></div><div><dt>最近更新</dt><dd>{{ selectedAsset.updated }}</dd></div><div><dt>使用状态</dt><dd>{{ selectedAsset.status }}</dd></div><div><dt>上传者</dt><dd>{{ selectedAsset.uploaderName || '未知成员' }}</dd></div><div><dt>操作权限</dt><dd>{{ canManageSelectedAsset ? '可编辑、可删除' : '仅可查看' }}</dd></div></dl>
                <div class="version-note"><ShieldCheck :size="18" /><div><b>版本保护已开启</b><p>替换当前素材时会自动保留前一个版本，方便团队随时恢复。</p></div></div>
              </template>
              <div class="dialog-actions"><template v-if="assetEditing"><button class="button outline" :disabled="busy" @click="cancelAssetEdit">取消</button><button class="button primary profile-save-button" :disabled="busy || !assetDraft.title.trim()" @click="saveAssetDetails"><RefreshCw v-if="busy" class="spin-inline" :size="15" /><Check v-else :size="15" />{{ busy ? '正在保存…' : '保存修改' }}</button></template><template v-else><span v-if="!canManageSelectedAsset" class="permission-note"><LockKeyhole :size="13" />仅上传者或管理员可操作</span><a class="button outline" :href="isGalleryCategory(selectedAsset.category) ? characterImageSrc : selectedAsset.image" target="_blank" rel="noopener"><Eye :size="15" />查看原图</a><button v-if="canManageSelectedAsset" class="button edit-profile-button" @click="startAssetEdit"><PencilLine :size="15" />编辑</button><button v-if="canManageSelectedAsset" class="button primary" @click="cycleAsset(selectedAsset)">更改状态</button><button v-if="canManageSelectedAsset" class="button danger-button" @click="requestAssetDelete"><Trash2 :size="15" />删除</button></template></div>
            </div>
            <Transition name="save-confirm">
              <div v-if="profileSaveNotice" class="profile-save-confirm" role="status" aria-live="polite">
                <span><Check :size="18" /></span><div><b>保存成功</b><small>{{ profileSaveNotice }}</small></div>
              </div>
            </Transition>
            <div v-if="characterImageActionConfirm && characterImageActionTarget" class="delete-confirm character-image-confirm" role="alertdialog" aria-modal="true" :aria-label="characterImageActionConfirm.type === 'replace' ? '确认更换素材文件' : '确认删除素材文件'">
              <div>
                <span class="character-image-confirm-preview"><video v-if="galleryMediaKind(characterImageActionTarget.src) === 'video'" :src="characterImageActionTarget.src" muted></video><span v-else-if="galleryMediaKind(characterImageActionTarget.src) === 'audio'" class="confirm-audio-file"><Film :size="34" />音频文件</span><img v-else :src="characterImageActionTarget.src" :alt="`第 ${characterImageActionTargetIndex + 1} 张素材图片`" /></span>
                <h3>{{ characterImageActionConfirm.type === 'replace' ? `确认更换第 ${characterImageActionTargetIndex + 1} 个文件？` : `确认删除第 ${characterImageActionTargetIndex + 1} 个文件？` }}</h3>
                <p>{{ characterImageActionConfirm.type === 'replace' ? `确认后请选择新${selectedAsset.category === '视频音频' ? '视频或音频文件' : '图片'}，左侧该位置将显示替换后的预览。` : `这个文件将从${selectedAsset.category}${selectedAsset.category === '视频音频' ? '文件' : '图片'}列表移除，点击保存后才会正式删除。` }}</p>
                <div><button class="button outline" type="button" @click="characterImageActionConfirm = null">取消</button><button class="button" :class="characterImageActionConfirm.type === 'replace' ? 'primary' : 'danger-solid'" type="button" @click="confirmCharacterImageAction">{{ characterImageActionConfirm.type === 'replace' ? `选择新${selectedAsset.category === '视频音频' ? '文件' : '图片'}` : '确认删除' }}</button></div>
              </div>
            </div>
            <div v-if="deleteConfirmOpen" class="delete-confirm" role="alertdialog" aria-modal="true" aria-label="确认删除素材">
              <div><span class="delete-icon"><Trash2 :size="22" /></span><h3>确定删除这项素材？</h3><p>“{{ selectedAsset.title }}”及其上传文件将被永久删除，此操作无法撤销。</p><div><button class="button outline" @click="deleteConfirmOpen = false">取消</button><button class="button danger-solid" :disabled="busy" @click="confirmAssetDelete">{{ busy ? '正在删除' : '确认删除' }}</button></div></div>
            </div>
          </section>
          <Transition name="image-preview">
            <div v-if="imagePreviewOpen" class="asset-lightbox" role="dialog" aria-modal="true" :aria-label="`大图预览：${selectedAsset.title}`" @click.self="closeImagePreview">
              <button class="asset-lightbox-close" type="button" aria-label="关闭大图预览" @click="closeImagePreview"><X :size="22" /></button>
              <div class="asset-lightbox-canvas" :class="{ zoomed: imagePreviewZoomed }">
                <video v-if="selectedGalleryMediaKind === 'video'" :src="imagePreviewSrc" controls autoplay></video>
                <div v-else-if="selectedGalleryMediaKind === 'audio'" class="lightbox-audio-player"><Film :size="48" /><strong>{{ selectedAsset.title }}</strong><audio :src="imagePreviewSrc" controls autoplay></audio></div>
                <button v-else type="button" :aria-label="imagePreviewZoomed ? '缩小至适合屏幕' : '查看原始尺寸'" @click="imagePreviewZoomed = !imagePreviewZoomed">
                  <img :src="imagePreviewSrc" :alt="selectedAsset.title" @error="handleAssetImageError" />
                </button>
              </div>
              <footer><div><strong>{{ selectedAsset.title }}</strong><small>{{ selectedAsset.category }} · {{ isGalleryCategory(selectedAsset.category) ? `第 ${selectedCharacterImageIndex + 1} 个文件` : selectedAsset.version }}</small></div><span v-if="selectedGalleryMediaKind === 'image'">{{ imagePreviewZoomed ? '点击图片适应屏幕' : '点击图片查看原始尺寸' }}</span><span v-else>使用播放器控制播放进度和音量</span></footer>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
    <Transition name="toast"><div v-if="toast" class="toast"><Check :size="16" />{{ toast }}<button aria-label="关闭" @click="toast = ''"><X :size="14" /></button></div></Transition>
  </main>
</template>
